import merge from 'lodash/merge';
import round from 'lodash/round';
import isEqual from 'lodash/isEqual';
import moment from 'moment';

// Redux
import { Action, Result, ApiAction, AsyncResult, Dispatch } from '../../index';
import api from '../../redux-api';
import { historicalScenariosApi } from '../../api';
import { SET_COMPARISON_IS_FOCUSED, TOGGLE_PORTFOLIO_EDITOR } from '../common/actions';
import { resetCurrentContact, resetGif, setComparisonIsFocused, getBenchmarksData, loadBenchmarkForEdit } from '../common/actionCreators';
import { cacheFundsData, getFundData, getManyFundsData } from '../funds/fund-store';
import { getFundsBenchmarks, getFundBenchmarks } from '../benchmarks/benchmark-store';
import {
  loadPortfolioForEdit,
  getPortfolioHoldingsData,
  addFundToPortfolioById,
  addStockToPortfolioById,
  registerOnPortfolioCancel,
  setExpandComparisonItem,
  calculateCustomFund,
  addAltToPortfolioById,
} from '../edit-portfolio';
import { getStockData, getManyStocksData } from '../stocks/stock-store';
import { getPortfolioById } from '../portfolios';
import {
  getComparisonBenchmarkData,
  getComparisonBenchmark,
  getStockBenchmarks,
  getStocksBenchmarks,
  getManyComparisonBenchmarkData,
} from '../comparison-benchmarks/comparison-benchmarks-store';

import { getAltData, getManyAltsData } from '../alts/alts-store';
import { getBenchmarkPortfolioById } from '../comparison-benchmarks/benchmark-portfolios';
import { getUserInfos } from '../users';
import { addSearchItemLog } from '../activity-log';
import { setComparisonCorrelations } from '../comparison-correlations';
import { refreshProposalItems as refreshProposalItemsV2 } from '../proposal-v2/actions';
import { startLoaderAction, stopLoaderAction } from '../ui/loaders';
import { resetQuestionnaire } from '../questionnaire';
import { clearSuitabilityAnalysis } from '../suitability-analysis';
import { updateEmbeddedContactComparison } from '../contacts';

import {
  logAddComparisonMarkup,
  logAddToComparison,
  logAttachOmniRiskProfileToComparison,
  logAttachRiskProfile,
  logNewComparison,
  logNewPortfolio,
  logRemoveFromComparison,
  logResetComparisonMarkup,
} from '../../../services/usage-log';

// Utils
import {
  fundToComparisonItem,
  generateNewItemColor,
  portfolioToComparisonItem,
  stockToComparisonItem,
  generateItemCopyName,
  generateDraftComparison,
  mapComparisonId,
  comparisonIsReadOnly,
  generateSharedComparison,
  getCurrentStateComparison,
  benchmarkToComparisonItem,
  isBenchmark,
  getDuplicateFields,
  overrideRiskMetrics,
  COMPARISON_TYPE,
  comparisonShouldForceMonthlyData,
  updateChartDatesToToday,
  getMonthEndDataOption,
  refreshComparisonGroupItemsHoldings,
  getComparisonFilteredItems,
  setBenchmarkRisk,
  shouldUseMonthlyRollingInReturns,
  comparisonHasMissingOrModifiedItems,
  filterModifiedItemsByKeys,
  isUnallocatedPortfolioModification,
  isPlanRemovedPortfolioModification,
} from '../../../utils/comparison';
import { generateTempId, generateBSONId } from '../../../utils/random';
import { replaceById, deepClone, diffArray } from '../../../utils/objects';
import { formatLocalizedString, formatMessage } from '../../../utils/i18n';
import { getSharingInfo, getInitialUserPreferences } from '../../../utils/users';
import {
  MARKUP_TOOLS,
  selectCategoryFields,
  fieldIsSelected,
  getAvailableFields,
  deleteUnpostedComments,
  highlightField,
  updateMarkupComment,
  outlineField,
} from '../../../utils/markup-tools';
import {
  comparisonBenchmarkToBenchmark,
  benchmarkIsFull,
  calculateBenchmarkPortfolioStats,
  createPortfolioCategoryBenchmark,
  getCategoryBenchmarksMap,
  benchmarkToBenchmarkPortfolio,
} from '../../../utils/comparison/benchmarks';
import { updateAccountSetsAfterItemRemoval, sanitizeAccountSetsForSave } from '@/utils/comparison/account-sets';
import { remapAccountSetIndices } from '@/modules/proposal/utils/account-set-helpers';

import {
  PERFORMANCE_CHART,
  MER_FIELDS,
  DISABLED_MER_FIELDS,
  COMPARISON_CATEGORIES,
  COMPARISON_ITEM_TYPES,
  NAV_PATHS,
  MAX_LOADED_COMPARISONS,
  TIME_PERIODS,
  BOND_TAG,
  ComparisonFlagsType,
} from '../../../utils/constants';
import { createPortfolioClone } from '../../../utils/portfolio/utils';
import { getCachedCustomYieldFund } from '@/utils/portfolio/customYieldCache';
import { getEmptyPortfolio, HOLDING_TYPES, PortfolioData } from '../../../utils/portfolios';
import { SEARCH_ITEM_SOURCE, SEARCH_ITEM_DEST } from '../../../utils/activity-log';
import { createComparisonContact } from '../../../utils/contacts';

import {
  calculateIncomeStreamData,
  SCENARIO_UNIT,
  SCENARIO_TYPE,
  SCENARIO_FREQUENCY,
  getIncomeStartDate,
  validIncomeStreamItem,
  getIncomeEndDate,
} from '../../../utils/income-stream';
import { reallocateAndRecalculatePortfolio } from '../../../utils/portfolio/calculate';
import { isInvalidDepositAge } from '../../../utils/bmo-gif';
import sentryUtils from '../../../utils/3rd-party/sentry';
import { userHasPermission, PERMISSIONS } from '../../../utils/permissions';
import LogRocketUtils from '../../../utils/3rd-party/logrocketUtils';
import { QUESTIONNAIRE_TYPES } from '../../../utils/questionnaire';
import { getCommonInception } from '@/utils/common-inception';
import { getMaxEndDate } from '@/utils/dates';

// Types
import { ComparisonState, ComparisonsListState, ComparisonItemsFilter } from '../types/comparison';
import { selectCurrentTaxRate } from '../taxes/selectors';
import { LocationState } from 'history';
import { recalcBenchmarkItem, recalcPortfolioItem } from './item-calculation';
import { refreshComparisonData } from './item-refresh';
import { combineProductsIntoGroupItem } from './item-group';
import { USER_PREFERENCES_INITIAL_BENCHMARK } from '../user-preferences';
import Dictionary from '@/types/dictionary';
import Nullable from '@/types/nullable';
import QuickSearchItem from '@/types/quick-search-item';
import Comparison from '@/types/comparison';
import { HoldingTaxMetrics, PortfolioTaxMetrics, TaxSettingsUpdate } from '@/types/tax-analysis';
import Fund from '@/types/fund';
import Stock from '@/types/stock';
import Benchmark from '@/types/benchmark';
import ComparisonItem from '@/types/comparison-item';
import { ComparisonItemCategory } from '@/redux/modules/types/comparison';
import ReducedComparison from '@/types/reduced-comparison';
import ComparisonChartOptions from '@/types/comparison-chart-options';
import Locale from '@/types/locale';
import Portfolio from '@/types/portfolio';
import ComparisonCategory from '@/types/comparison-category';
import ComparisonField from '@/types/comparison-field';
import SelectedCategories from '@/types/selected-categories';
import BasicUserInfos from '@/types/basic-user-infos';
import AppRegion from '@/types/app-region';
import ComparisonBenchmark from '@/types/comparison-benchmark';
import BenchmarkPortfolio from '@/types/benchmark-portfolio';
import GenericContact from '@/types/generic-contact';
import ComparisonContact from '@/types/comparison-contact';
import SharedComparisonInfos from '@/types/shared-comparison-infos';
import RiskProfileItem from '@/types/risk-profile-item';
import SharingInfo from '@/types/sharing-info';
import ComparisonHolding from '@/types/comparison-holding';
import IncomeStreamScenario from '@/types/income-stream-scenario';
import ComparisonGroupProduct from '@/types/comparison-group-product';
import User from '@/types/user';
import BMOGIFItem from '@/types/bmo-gif/bmo-gif-item';
import PERIOD_END_DATA_OPTIONS from '@/types/period-end-data-options';
import CommonInception from '@/types/common-inception';
import { monthsToPeriod } from '@/utils/time';
import dom from '@/utils/dom';
import { FormattedFullOmniRiskProfile } from '@/types/risk-profiles/omni/full-omni-risk-profile';
import { OmniRiskProfileConfig } from '@/types/models/risk-profiles/omni-risk-profiles-config';
import { GenericHouseHold } from '@/types/households';
import { clearCurrentHousehold } from '../household';
import Alt from '@/types/models/alt';
import altToComparisonItem from '@/utils/comparison/instrument-to-comparison-item/alt-to-comparison-item';
import { COMPARISON_MODIFIED_ITEM_KEYS } from '@/enums/Comparison';
import { hasComparisonFlag } from './utils/hasComparisonFlag';
import HistoricalScenario from '@/types/historical-scenario';
import CalculatedHistoricalScenario from '@/types/calculated-historical-scenario';
import Shelf from '@/types/shelf';
import AccountSet from '@/types/comparison-account-set';

export type HoldingsData = { funds: Fund[]; stocks: Stock[]; alts: Alt[]; benchmarks: Benchmark[] };

const INITIAL_COMPARISONS_CACHE = {
  recent: {
    list: [],
  },
  assigned: {
    list: [],
  },
  drafts: {
    list: [],
  },
  compliance: {
    list: [],
  },
};

const getInitialState = (): ComparisonState =>
  // Initial State
  ({
    activeComparison: undefined,
    loading: {
      isLoading: false,
    },
    selectedComparisonItems: [],
    comparisons: INITIAL_COMPARISONS_CACHE,
    skipEmptyState: false,
    isSaving: false,
    forcedMonthlyData: false,
    isFocused: false,
    showLoadClientPortfoliosModal: false,
    shouldUpdateCommonInception: false,
    itemsFilter: [ComparisonItemsFilter.All],
    portfolioEditor: {
      isVisible: false,
      isPinned: false,
      isReadOnly: false,
    },
  });
// Transform response by converting string dates to javascript dates
const transformComparison = (comparison: Comparison) => {
  const transformDate = (date?: Date | string | null) => {
    if (date === null) {
      return null;
    }

    return date ? new Date(date) : undefined;
  };

  const transformPeriod = (obj: { monthsRange?: number; period?: TIME_PERIODS }) => {
    if (obj.period !== undefined) {
      return obj.period;
    }

    if (obj.monthsRange !== undefined) {
      return monthsToPeriod(obj.monthsRange);
    }

    return undefined;
  };

  return {
    ...comparison,
    lastRunAt: comparison.lastRunAt ? transformDate(comparison.lastRunAt) : comparison.lastRunAt,
    options: comparison.options && {
      growth: comparison.options.growth && {
        ...comparison.options.growth,
        startDate: transformDate(comparison.options.growth.startDate),
        endDate: transformDate(comparison.options.growth.endDate),
      },
      fundGrowth: comparison.options.fundGrowth && {
        ...comparison.options.fundGrowth,
        startDate: transformDate(comparison.options.fundGrowth.startDate),
        endDate: transformDate(comparison.options.fundGrowth.endDate),
      },
      portfolioGrowth: comparison.options.portfolioGrowth && {
        ...comparison.options.portfolioGrowth,
        startDate: transformDate(comparison.options.portfolioGrowth.startDate),
        endDate: transformDate(comparison.options.portfolioGrowth.endDate),
      },
      groupGrowth: comparison.options.groupGrowth && {
        ...comparison.options.groupGrowth,
        startDate: transformDate(comparison.options.groupGrowth.startDate),
        endDate: transformDate(comparison.options.groupGrowth.endDate),
      },
      riskReward: comparison.options.riskReward && {
        period: transformPeriod(comparison.options.riskReward),
      },
      drawdown: comparison.options.drawdown && {
        ...comparison.options.drawdown,
        startDate: transformDate(comparison.options.drawdown.startDate),
        endDate: transformDate(comparison.options.drawdown.endDate),
      },
      income: comparison.options.income && {
        ...comparison.options.income,
        startDate: transformDate(comparison.options.income.startDate),
        endDate: transformDate(comparison.options.income.endDate),
      },
    },
  } as Comparison;
};

const transform = (apiResponse: { data: Comparison }) => transformComparison(apiResponse.data);

// Actions
const SET_SHOULD_UPDATE_COMMON_INCEPTION: Action = {
  type: 'connect/comparison/set-should-update-common-inception',
};

const SET_ITEMS: Action = {
  type: 'connect/comparison/set-items',
};

const ADD_ITEM: Action = {
  type: 'connect/comparison/add-item',
};

export const REMOVE_ITEM: Action = {
  type: 'connect/comparison/remove-item',
};

const REPLACE_ITEM: Action = {
  type: 'connect/comparison/replace-item',
};

export const SAVE_COMPARISON: ApiAction = {
  endpoint: 'comparisons/',
  type: 'connect/comparison/save',
  transform,
};

const POST_SAVE_COMPARISON: Action = {
  type: 'connect/comparison/post-save',
};

const UPDATE_COMPARISON: ApiAction = {
  endpoint: 'comparisons/:id/',
  type: 'connect/comparison/update',
  transform,
};

export const UPDATE_COMPARISON_TAX_SETTINGS: Action = {
  type: 'connect/comparison/update-tax-settings',
};

export const SET_TAX_ANALYSIS_DATA: Action = {
  type: 'connect/comparison/set-tax-analysis-data',
};

export const SET_TAX_ANALYSIS_COMPARISON_ITEMS: Action = {
  type: 'connect/comparison/set-tax-analysis-comparison-items',
};

export const SET_TAX_ANALYSIS_HOLDINGS_DATA: Action = {
  type: 'connect/comparison/set-tax-analysis-holdings-data',
};

const CREATE_OMNI_EXPORT: ApiAction = {
  endpoint: 'comparisons/:id/create-export/:contactId/',
  type: 'connect/comparison/create-omni-export',
};

const CREATE_OMNI_EXPORT_NO_CONTACT: ApiAction = {
  endpoint: 'comparisons/:id/create-export/',
  type: 'connect/comparison/create-omni-export',
};

export const LOAD_COMPARISON: Action = {
  type: 'connect/comparison/load',
};

export const DELETE_COMPARISON: ApiAction = {
  endpoint: 'comparisons/:id/',
  type: 'connect/comparison/delete',
};

const SET_COMPARISON_NAME: Action = {
  type: 'connect/comparison/edit-name',
};

const SET_COMPARISON_CONTACT: Action = {
  type: 'connect/comparison/set-contact',
};

export const CLOSE_COMPARISON: Action = {
  type: 'connect/comparison/close',
};

const GET_COMPARISON_BY_ID: ApiAction = {
  type: 'connect/comparison/get-by-id',
  endpoint: 'comparisons/:id/',
  transform,
};

const SET_COMMENT: Action = {
  type: 'connect/comparison/set-comment',
};

const SHARE_COMPARISON: ApiAction = {
  endpoint: 'comparisons/share',
  type: 'connect/comparison/share',
};

const VALIDATE_COMPARISON_TOKEN: ApiAction = {
  endpoint: 'comparisons/validate-token/:token/',
  type: 'connect/comparison/validate-id',
};

const UNSELECT_ALL_COMPARISON_ITEMS: Action = {
  type: 'connect/comparison/unselect-all-items',
};

const ADD_RATIONALE_ANSWERS: Action = {
  type: 'connect/comparison/add-rationale-answers',
};

const ATTACH_OMNI_RISK_PROFILE: Action = {
  type: 'connect/comparison/attach-omni-risk-profile',
};

const ATTACH_RISK_PROFILE: Action = {
  type: 'connect/comparison/attach-risk-profile',
};

const ATTACH_PORTFOLIO_RISK_PROFILE: Action = {
  type: 'connect/comparison/attach-portfolio-risk-profile',
};

const CLEAR_RISK_PROFILE: Action = {
  type: 'connect/comparison/clear-risk-profile',
};

const CLEAR_PORTFOLIO_RISK_PROFILE: Action = {
  type: 'connect/comparison/clear-portfolio-risk-profile',
};

const SET_SELECTED_CATEGORIES: Action = {
  type: 'connect/comparison/set-selected-categories',
};

const SELECT_CATEGORY: Action = {
  type: 'connect/comparison/select-category',
};

const SET_TOGGLED_CATEGORIES: Action = {
  type: 'connect/comparison/toggle-category',
};

const SET_EXPANDED_FIELDS: Action = {
  type: 'comparison/toggle-field-expansion',
};

const TOGGLE_FIELD: Action = {
  type: 'connect/comparison/toggle-field',
};

const APPLY_MARKUP: Action = {
  type: 'connect/comparison/apply-markup',
};

const CLEAR_ALL_MARKUP: Action = {
  type: 'connect/comparison/clear-all-markup',
};

const CLEAR_MARKUP: Action = {
  type: 'connect/comparison/clear-markup',
};

const SET_MARKUP_COMMENT: Action = {
  type: 'connect/comparison/set-markup-comment',
};

const POST_MARKUP_COMMENT: Action = {
  type: 'connect/comparison/post-markup-comment',
};

const SET_SHARED_BY_USER_INFOS: Action = {
  type: 'connect/comparison/shared-by-infos',
};

const SET_RISK_PERIOD: Action = {
  type: 'connect/comparison/set-risk-period',
};

const SET_FUND_RISK_PERIOD: Action = {
  type: 'connect/comparison/set-fund-risk-period',
};

const SET_PORTFOLIO_RISK_PERIOD: Action = {
  type: 'connect/comparison/set-portfolio-risk-period',
};

const SET_GROUP_RISK_PERIOD: Action = {
  type: 'connect/comparison/set-group-risk-period',
};

const SET_CALENDAR_YEAR_PERIOD: Action = {
  type: 'connect/comparison/set-calendar-year-period',
};

const SET_FUND_CALENDAR_YEAR_PERIOD: Action = {
  type: 'connect/comparison/set-fund-calendar-year-period',
};

const SET_PORTFOLIO_CALENDAR_YEAR_PERIOD: Action = {
  type: 'connect/comparison/set-portfolio-calendar-year-period',
};

const SET_GROUP_CALENDAR_YEAR_PERIOD: Action = {
  type: 'connect/comparison/set-group-calendar-year-period',
};

const SET_CHART_OPTIONS: Action = {
  type: 'connect/comparison/set-chart-options',
};

const SET_SELECTED_ITEMS: Action = {
  type: 'connect/comparison/set-selected-items',
};

const SET_CALCULATION_BENCHMARK: Action = {
  type: 'connect/comparison/set-calculation-benchmark',
};

const GET_RECENT_COMPARISONS: ApiAction = {
  type: 'connect/comparisons/get-recent',
  endpoint: 'comparisons',
};

const GET_ASSIGNED_COMPARISONS: ApiAction = {
  type: 'connect/comparisons/get-assigned',
  endpoint: 'comparisons',
};

const GET_DRAFT_COMPARISONS: ApiAction = {
  type: 'connect/comparisons/get-drafts',
  endpoint: 'comparisons',
};

const GET_COMPLIANCE_COMPARISONS: ApiAction = {
  type: 'connect/comparisons/get-compliance-comparisons',
  endpoint: 'comparisons',
};

const CLEAR_COMPARISONS_CACHE: Action = {
  type: 'connect/comparisons/clear-cache',
};

const SET_BMO_GIF_ITEMS: Action = {
  type: 'connect/comparisons/set-bmo-gif-item',
};

const SKIP_EMPTY_STATE: Action = {
  type: 'connect/comparisons/skip-empty-state',
};

const SET_SHOW_IN_BASE_CURRENCY: Action = {
  type: 'connect/comparisons/set-show-base-currency',
};

const SET_MONTH_END_DATA_OPTION: Action = {
  type: 'connect/comparisons/set-month-end-data-option',
};

const LOAD_DEFAULT_INCOME_STREAM: Action = {
  type: 'connect/comparison/load-default-income-stream',
};

const REFRESH_ITEM: Action = {
  type: 'connect/comparison/refresh-item',
};

const LOAD_SCENARIO_FOR_EDIT: Action = {
  type: 'connect/comparison/load-income-stream-scenario',
};

const UPDATE_SCENARIO: Action = {
  type: 'connect/comparison/update-scenario',
};

const CANCEL_SCENARIO_EDITING: Action = {
  type: 'connect/comparison/cancel-scenario-editing',
};

const SET_SHORT_HISTORY: Action = {
  type: 'connect/comparisons/set-short-history',
};

const SET_IS_SAVING: Action = {
  type: 'connect/comparisons/set-is-saving',
};

const SET_IS_LOADING: Action = {
  type: 'connect/comparisons/set-is-loading',
};

const SET_IS_DIRTY: Action = {
  type: 'connect/comparisons/set-is-dirty',
};

const SET_TAB: Action = {
  type: 'connect/comparisons/set-tab',
};

const SET_FORCED_MONTHLY_DATA: Action = {
  type: 'connect/comparisons/set-forced-monthly-data',
};

const SET_COMMON_INCEPTION_ITEM: Action = {
  type: 'connect/comparisons/set-common-inception-item',
};

export const ADD_FUNDS_TO_COMPARISON: Action = {
  type: 'connect/comparisons/add-funds-to-comparison',
};

export const ADD_PORTFOLIOS_TO_COMPARISON: Action = {
  type: 'connect/comparisons/add-portfolios-to-comparison',
};

export const ADD_BENCHMARK_PORTFOLIOS_TO_COMPARISON: Action = {
  type: 'connect/comparisons/add-benchmark-portfolios-to-comparison',
};

const SET_ITEM_HOLDING: Action = {
  type: 'connect/comparisons/set-item-holding',
};

export const SET_IS_RERUNNING: Action = {
  type: 'connect/comparisons/set-is-rerunnning',
};

export const SET_COMPARISON_ITEMS_FILTER: Action = {
  type: 'connect/comparisons/set-items-filter',
};

export const CHANGE_COMPARISON_ITEMS_ORDER: Action = {
  type: 'connect/comparisons/change-items-order',
};

export const CHANGE_ITEM_ID: Action = {
  type: 'connect/comparisons/change-item-id',
};

export const ADD_ALT_TO_COMPARISON: Action = {
  type: 'connect/comparisons/add-alt-to-comparison',
};

export const ADD_FUND_TO_COMPARISON: Action = {
  type: 'connect/comparisons/add-fund-to-comparison',
};

export const ADD_PORTFOLIO_TO_COMPARISON: Action = {
  type: 'connect/comparisons/add-portfolio-to-comparison',
};

export const ADD_STOCK_TO_COMPARISON: Action = {
  type: 'connect/comparisons/add-stock-to-comparison',
};

export const ADD_BENCHMARK_TO_COMPARISON: Action = {
  type: 'connect/comparisons/add-benchmark-to-comparison',
};

export const SET_IS_ATTACHING_OMNI_RISK_PROFILE: Action = {
  type: 'connect/comparisons/set-is-attaching-omni-risk-profile',
};

export const SET_IS_ATTACHING_RISK_PROFILE: Action = {
  type: 'connect/comparisons/set-is-attaching-risk-profile',
};

export const SET_IS_ATTACHING_PORTFOLIO_RISK_PROFILE: Action = {
  type: 'connect/comparisons/set-is-attaching-portfolio-risk-profile',
};

export const SET_COMPARISON_ITEM_FLAGS: Action = {
  type: 'connect/comparisons/set-item-flags',
};

export const SET_ORIGIN_PATH: Action = {
  type: 'connect/comparisons/set-origin-path',
};

export const SET_PROPOSAL_LOCALE: Action = {
  type: 'connect/comparisons/set-proposal-locale',
};

export const SET_SHOW_LOAD_CLIENT_PORTFOLIO_MODAL: Action = {
  type: 'connect/comparisons/set-show-load-portfolio-modal',
};

export const SET_SELECTED_HISTORICAL_SCENARIOS: Action = {
  type: 'connect/comparisons/set-selected-historical-scenarios',
};

export const UPDATE_COMPARISON_ACCOUNT_SETS: Action = {
  type: 'connect/comparison/update-account-sets',
};

export const UPDATE_SELECTED_HISTORICAL_SCENARIO: Action = {
  type: 'connect/comparisons/update-selected-historical-scenario',
};

export const REMOVE_SELECTED_HISTORICAL_SCENARIO: Action = {
  type: 'connect/comparisons/remove-selected-historical-scenario',
};

export const SHARED_WITH_CLIENT: ApiAction = {
  endpoint: 'comparisons/:id/shared-with-client',
  type: 'connect/comparison/shared-with-client',
};

export const CALCULATE_COMPARISON_HISTORICAL_SCENARIOS: ApiAction = {
  endpoint: 'comparisons/:id/historical-scenarios',
  type: 'connect/comparison/calculate-comparison-historical-scenarios',
};

export const CALCULATE_HISTORICAL_SCENARIOS: ApiAction = {
  endpoint: 'historical-scenarios/calculate',
  type: 'connect/comparison/calculate-historical-scenarios',
};

export const PROCESSING_COMPARISON_ITEM_ACTION: Action = {
  type: 'connect/comparison/processing-comparison-item-action',
};

/**
 * The actions listed here are actions that directly affect the comparison
 * builder. If those actions get dispatched after the current comparison has
 * been closed, an empty comparison object will get recreated which shouldn't
 * happen - if any of those actions get dispatched when no active comparison
 * is found, we'll instead prevent any state update at all.
 */
const ACTIVE_COMPARISON_ACTIONS = [
  SET_ITEMS.type,
  ADD_ITEM.type,
  REMOVE_ITEM.type,
  SET_COMMENT.type,
  SET_COMPARISON_NAME.type,
  SET_COMPARISON_CONTACT.type,
  CLOSE_COMPARISON.type,
  ADD_RATIONALE_ANSWERS.type,
  ATTACH_OMNI_RISK_PROFILE.type,
  ATTACH_RISK_PROFILE.type,
  ATTACH_PORTFOLIO_RISK_PROFILE.type,
  SET_TOGGLED_CATEGORIES.type,
  SET_SELECTED_CATEGORIES.type,
  SELECT_CATEGORY.type,
  TOGGLE_FIELD.type,
  SET_CALENDAR_YEAR_PERIOD.type,
  SET_FUND_CALENDAR_YEAR_PERIOD.type,
  SET_PORTFOLIO_CALENDAR_YEAR_PERIOD.type,
  SET_GROUP_CALENDAR_YEAR_PERIOD.type,
  SET_RISK_PERIOD.type,
  SET_FUND_RISK_PERIOD.type,
  SET_PORTFOLIO_RISK_PERIOD.type,
  SET_GROUP_RISK_PERIOD.type,
  CLEAR_RISK_PROFILE.type,
  CLEAR_PORTFOLIO_RISK_PROFILE.type,
  SET_SHARED_BY_USER_INFOS.type,
  SET_CHART_OPTIONS.type,
  SET_SELECTED_ITEMS.type,
  SET_CALCULATION_BENCHMARK.type,
  SET_BMO_GIF_ITEMS.type,
  SET_SHOW_IN_BASE_CURRENCY.type,
  SET_MONTH_END_DATA_OPTION.type,
  LOAD_DEFAULT_INCOME_STREAM.type,
  REFRESH_ITEM.type,
  SET_SHORT_HISTORY.type,
  SET_IS_DIRTY.type,
  APPLY_MARKUP.type,
  CLEAR_ALL_MARKUP.type,
  CLEAR_MARKUP.type,
  SET_MARKUP_COMMENT.type,
  POST_MARKUP_COMMENT.type,
  ADD_FUNDS_TO_COMPARISON.type,
  ADD_PORTFOLIOS_TO_COMPARISON.type,
  ADD_BENCHMARK_PORTFOLIOS_TO_COMPARISON.type,
  SET_IS_RERUNNING.type,
  ADD_ALT_TO_COMPARISON.type,
  ADD_FUND_TO_COMPARISON.type,
  ADD_PORTFOLIO_TO_COMPARISON.type,
  ADD_STOCK_TO_COMPARISON.type,
  ADD_BENCHMARK_TO_COMPARISON.type,
  SET_IS_ATTACHING_OMNI_RISK_PROFILE.type,
  SET_IS_ATTACHING_RISK_PROFILE.type,
  SET_IS_ATTACHING_PORTFOLIO_RISK_PROFILE.type,
  POST_SAVE_COMPARISON.type,
  SET_COMMON_INCEPTION_ITEM.type,
  SET_SHOULD_UPDATE_COMMON_INCEPTION.type,
  SET_SELECTED_HISTORICAL_SCENARIOS.type,
  UPDATE_COMPARISON_ACCOUNT_SETS.type,
];

// Reducer
export default function reducer(state: ComparisonState = getInitialState(), action: Action): ComparisonState {
  const hasActiveComparison = getCurrentStateComparison(state) != null;
  const currentComparison = { ...getCurrentStateComparison(state) };
  const benchmarkId = currentComparison.selectedBenchmarkId;

  // These actions require an active comparison - if none are found, we'll prevent any state update.
  if (!hasActiveComparison) {
    const actionType = action.type.replace(/(_SUCCESS|_ERROR)$/, '');
    if (ACTIVE_COMPARISON_ACTIONS.includes(actionType)) {
      return state;
    }
  }

  switch (action.type) {
    case `${GET_RECENT_COMPARISONS.type}_SUCCESS`: {
      const comparisons = action.data.data.comparisons.map(transformComparison);

      return {
        ...state,
        comparisons: {
          ...state.comparisons,
          recent: {
            ...(state.comparisons as Dictionary<ComparisonsListState>).recent,
            list: action.params.next ? (state.comparisons as Dictionary<ComparisonsListState>).recent.list.concat(comparisons) : comparisons,
            nextToken: action.data.data.next,
          },
        },
      };
    }
    case `${GET_ASSIGNED_COMPARISONS.type}_SUCCESS`: {
      const comparisons = action.data.data.comparisons.map(transformComparison);

      return {
        ...state,
        comparisons: {
          ...state.comparisons,
          assigned: {
            ...(state.comparisons as Dictionary<ComparisonsListState>).assigned,
            list: action.params.next ? (state.comparisons as Dictionary<ComparisonsListState>).assigned.list.concat(comparisons) : comparisons,
            nextToken: action.data.data.next,
          },
        },
      };
    }
    case `${GET_DRAFT_COMPARISONS.type}_SUCCESS`: {
      const comparisons = action.data.data.comparisons.map(transformComparison);

      return {
        ...state,
        comparisons: {
          ...state.comparisons,
          drafts: {
            ...(state.comparisons as Dictionary<ComparisonsListState>).drafts,
            list: action.params.next ? (state.comparisons as Dictionary<ComparisonsListState>).drafts.list.concat(comparisons) : comparisons,
            nextToken: action.data.data.next,
          },
        },
      };
    }
    case `${GET_COMPLIANCE_COMPARISONS.type}_SUCCESS`: {
      const comparisons = action.data.data.comparisons.map(transformComparison);

      return {
        ...state,
        comparisons: {
          ...state.comparisons,
          compliance: {
            ...(state.comparisons as Dictionary<ComparisonsListState>).compliance,
            list: action.params.next ? (state.comparisons as Dictionary<ComparisonsListState>).compliance.list.concat(comparisons) : comparisons,
            nextToken: action.data.data.next,
          },
        },
      };
    }
    case SET_ITEMS.type: {
      const items = action.items as ComparisonItem[];
      const benchmark = items.find(i => i._id === benchmarkId);

      if (benchmark) {
        setBenchmarkRisk(items, benchmark, currentComparison.commonInception, currentComparison.shortHistory);
      }

      currentComparison.items = items;
      currentComparison.isDirty = true;

      // Refresh the copy of the items used for selection
      const newSelectedItems = items.filter(i => state.selectedComparisonItems.some(selectedItem => selectedItem._id === i._id));

      return {
        ...state,
        activeComparison: currentComparison,
        selectedComparisonItems: newSelectedItems,
      };
    }
    case ADD_ITEM.type: {
      const item = action.item as ComparisonItem;
      const currentItems = currentComparison.items;
      const benchmark = currentItems.find(i => i._id === benchmarkId);

      if (benchmark) {
        setBenchmarkRisk([item], benchmark, currentComparison.commonInception);
      }

      const firstBenchmarkIndex = currentItems.findIndex(i => i.type === COMPARISON_ITEM_TYPES.BENCHMARK);
      const lastGroupIndex = currentItems.findIndex(i => i.type === COMPARISON_ITEM_TYPES.GROUP);

      if (item.type === COMPARISON_ITEM_TYPES.GROUP) {
        if (lastGroupIndex === -1) {
          // No existing group - add group at the start of the comparison.
          currentComparison.items = [item].concat(currentItems);
        } else {
          // Add item next to the last group
          const array = currentComparison.items.slice();
          array.splice(lastGroupIndex + 1, 0, item);
          currentComparison.items = array;
        }
      } else if (item.type !== COMPARISON_ITEM_TYPES.BENCHMARK && firstBenchmarkIndex !== -1) {
        const array = currentComparison.items.slice();
        array.splice(firstBenchmarkIndex, 0, item);
        currentComparison.items = array;
      } else {
        currentComparison.items = currentItems.concat(item);
      }

      currentComparison.isDirty = true;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case REMOVE_ITEM.type: {
      // Find the index of the item being removed before filtering
      const removedItemIndex = currentComparison.items.findIndex(item => item._id === action.itemId);

      currentComparison.items = currentComparison.items.filter(item => item._id !== action.itemId);
      currentComparison.isDirty = true;
      const { taxAnalysis } = currentComparison;

      if (taxAnalysis) {
        const { originalComparisonItemId, targetComparisonItemId } = taxAnalysis;
        if (originalComparisonItemId === action.itemId || targetComparisonItemId === action.itemId) {
          const newOriginalId = originalComparisonItemId !== action.itemId ? originalComparisonItemId : undefined;
          const newTargetId =
            originalComparisonItemId === action.itemId ? undefined : targetComparisonItemId !== action.itemId ? targetComparisonItemId : undefined;
          currentComparison.taxAnalysis = {
            ...taxAnalysis,
            originalComparisonItemId: newOriginalId,
            targetComparisonItemId: newTargetId,
          };
        }
      }

      if (removedItemIndex >= 0) {
        currentComparison.accountSets = updateAccountSetsAfterItemRemoval(currentComparison.accountSets, removedItemIndex);
      }

      return {
        ...state,
        activeComparison: currentComparison,
        selectedComparisonItems: state.selectedComparisonItems.filter(item => item._id !== action.itemId),
      };
    }
    case REPLACE_ITEM.type: {
      currentComparison.isDirty = true;
      currentComparison.items = currentComparison.items.map(item => {
        if (item._id !== action.item._id) {
          return item;
        }

        return {
          ...action.item,
          sharedFrom: item.sharedFrom,
          isExpanded: item.isExpanded,
          isFX: item.isFX,
          scenarios: item.scenarios,
        };
      });

      return {
        ...state,
        activeComparison: currentComparison,
        selectedComparisonItems: state.selectedComparisonItems.filter(item => item._id !== action.itemId),
      };
    }
    case POST_SAVE_COMPARISON.type: {
      return {
        ...state,
        activeComparison:
          currentComparison._id === action.id
            ? {
                ...currentComparison,
                lastRunAt: new Date(),
                isDirty: false,
              }
            : {
                ...currentComparison,
                lastRunAt: new Date(),
                isDirty: false,
                _id: action.id,
              },
      };
    }
    case `${DELETE_COMPARISON.type}_SUCCESS`: {
      const deletedId = action.params.id;

      return {
        ...state,
        comparisons: {
          ...state.comparisons,
          recent: {
            ...state.comparisons.recent,
            list: state.comparisons.recent.list.filter(c => c._id !== deletedId),
          },
          drafts: {
            ...state.comparisons.recent,
            list: state.comparisons.drafts.list.filter(c => c._id !== deletedId),
          },
          assigned: {
            ...state.comparisons.recent,
            list: state.comparisons.assigned.list.filter(c => c._id !== deletedId),
          },
        },
      };
    }
    case LOAD_COMPARISON.type: {
      const { comparison } = action;

      // If the comparison has an omni risk profile, we need to convert it to a risk profile
      if (comparison.omniRiskProfile && !comparison.riskProfile) {
        // The "riskProfile" key is deeply integrated with various features.
        // To keep the scope manageable, we leverage it to maintain compatibility rather than separating Correlate from the OMNI flow.
        comparison.riskProfile = {
          ...comparison.omniRiskProfile,
          isOmniRiskProfile: true,
        };
        delete comparison.omniRiskProfile;
      }

      return {
        ...state,
        activeComparison: comparison,
        loading: { isLoading: false },
        itemsFilter: [ComparisonItemsFilter.All],
      };
    }
    case SET_COMPARISON_NAME.type: {
      currentComparison.name = action.name;
      currentComparison.isDirty = true;
      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SET_COMPARISON_CONTACT.type: {
      currentComparison.contact = action.comparisonContact || undefined;
      currentComparison.comparisonType = COMPARISON_TYPE.CONTACT;
      currentComparison.isDirty = true;
      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case CLOSE_COMPARISON.type: {
      if (!state.activeComparison) {
        return state;
      }

      return {
        ...state,
        activeComparison: undefined,
        selectedComparisonItems: [],
        skipEmptyState: false,
        tab: undefined,
      };
    }
    case SET_COMMENT.type: {
      currentComparison.comment = action.comment;
      currentComparison.isDirty = true;
      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case UNSELECT_ALL_COMPARISON_ITEMS.type: {
      return {
        ...state,
        selectedComparisonItems: [],
      };
    }
    case ADD_RATIONALE_ANSWERS.type: {
      currentComparison.rationaleAnswersId = action.answersId;
      currentComparison.isDirty = true;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case ATTACH_OMNI_RISK_PROFILE.type: {
      // The "riskProfile" key is deeply integrated with various features.
      // To keep the scope manageable, we leverage it to maintain compatibility rather than separating Correlate from the OMNI flow.
      currentComparison.riskProfile = {
        ...action.profile,
        isOmniRiskProfile: true,
      };

      currentComparison.isDirty = true;
      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case ATTACH_RISK_PROFILE.type: {
      currentComparison.riskProfile = action.profile;
      currentComparison.isDirty = true;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case ATTACH_PORTFOLIO_RISK_PROFILE.type: {
      currentComparison.portfolioRiskProfile = action.profile;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SET_TOGGLED_CATEGORIES.type: {
      currentComparison.toggledCategories = action.toggledCategories;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SELECT_CATEGORY.type:
    case SET_SELECTED_CATEGORIES.type: {
      currentComparison.selectedCategories = action.selectedCategories;
      currentComparison.isDirty = true;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case TOGGLE_FIELD.type: {
      if (!currentComparison.selectedCategories) {
        return state;
      }

      currentComparison.selectedCategories[action.key].fields = action.fields;
      currentComparison.isDirty = true;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SET_EXPANDED_FIELDS.type: {
      currentComparison.expandedFields = action.expandedFields;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case APPLY_MARKUP.type:
    case CLEAR_ALL_MARKUP.type:
    case CLEAR_MARKUP.type:
    case SET_MARKUP_COMMENT.type:
    case POST_MARKUP_COMMENT.type: {
      currentComparison.items = replaceById(currentComparison.items, action.item, '_id');
      currentComparison.isDirty = true;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SET_RISK_PERIOD.type: {
      currentComparison.riskPeriod = action.period;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SET_FUND_RISK_PERIOD.type: {
      currentComparison.fundRiskPeriod = action.period;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SET_PORTFOLIO_RISK_PERIOD.type: {
      currentComparison.portfolioRiskPeriod = action.period;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SET_GROUP_RISK_PERIOD.type: {
      currentComparison.groupRiskPeriod = action.period;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SET_CALENDAR_YEAR_PERIOD.type: {
      currentComparison.calendarYearPeriod = action.period;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SET_FUND_CALENDAR_YEAR_PERIOD.type: {
      currentComparison.fundCalendarYearPeriod = action.period;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SET_PORTFOLIO_CALENDAR_YEAR_PERIOD.type: {
      currentComparison.portfolioCalendarYearPeriod = action.period;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SET_GROUP_CALENDAR_YEAR_PERIOD.type: {
      currentComparison.groupCalendarYearPeriod = action.period;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case CLEAR_RISK_PROFILE.type: {
      delete currentComparison.riskProfile;
      currentComparison.isDirty = true;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case CLEAR_PORTFOLIO_RISK_PROFILE.type: {
      delete currentComparison.portfolioRiskProfile;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SET_SHARED_BY_USER_INFOS.type: {
      currentComparison.sharedByInfos = action.userInfos;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SET_CHART_OPTIONS.type: {
      currentComparison.isDirty = true;
      currentComparison.options = {
        ...currentComparison.options,
        [action.chart]: {
          ...(currentComparison.options && currentComparison.options[action.chart as PERFORMANCE_CHART]),
          ...action.options,
        },
      };

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SET_SELECTED_ITEMS.type: {
      return {
        ...state,
        selectedComparisonItems: action.items,
      };
    }
    case SET_CALCULATION_BENCHMARK.type: {
      currentComparison.selectedBenchmarkId = action.benchmarkId;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case CLEAR_COMPARISONS_CACHE.type: {
      return {
        ...state,
        comparisons: INITIAL_COMPARISONS_CACHE,
      };
    }
    case SET_BMO_GIF_ITEMS.type: {
      currentComparison.gifItems = action.gifItems;
      currentComparison.isDirty = true;
      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SET_SHOW_IN_BASE_CURRENCY.type: {
      currentComparison.showBaseCurrencies = action.useBaseCurrency;
      currentComparison.isDirty = true;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SET_MONTH_END_DATA_OPTION.type: {
      currentComparison.monthEndDataOption = action.monthEndDataOptionValue;
      currentComparison.autoMonthEndData = action.autoMonthEndData;
      currentComparison.isDirty = true;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SKIP_EMPTY_STATE.type: {
      return {
        ...state,
        skipEmptyState: true,
      };
    }
    case LOAD_DEFAULT_INCOME_STREAM.type: {
      currentComparison.items = action.items;
      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case REFRESH_ITEM.type: {
      currentComparison.isDirty = true;
      currentComparison.items = replaceById(currentComparison.items, action.item, '_id');

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case LOAD_SCENARIO_FOR_EDIT.type:
    case UPDATE_SCENARIO.type: {
      return {
        ...state,
        editedScenario: action.scenario,
      };
    }
    case CANCEL_SCENARIO_EDITING.type: {
      return {
        ...state,
        editedScenario: undefined,
      };
    }
    case SET_SHORT_HISTORY.type: {
      currentComparison.shortHistory = action.shortHistory;
      currentComparison.isDirty = true;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SET_IS_SAVING.type: {
      return {
        ...state,
        isSaving: action.isSaving,
      };
    }
    case SET_IS_LOADING.type: {
      return {
        ...state,
        loading: {
          isLoading: action.isLoading,
          monthEndDataOption: action.monthEndDataOption,
        },
      };
    }
    case SET_IS_DIRTY.type: {
      currentComparison.isDirty = action.value;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SET_COMPARISON_IS_FOCUSED.type: {
      return {
        ...state,
        isFocused: action.isFocused,
      };
    }
    case TOGGLE_PORTFOLIO_EDITOR.type: {
      return {
        ...state,
        isFocused: action.isFocused,
        portfolioEditor: {
          isVisible: action.isVisible,
          isPinned: action.isPinned,
          isReadOnly: action.isReadOnly,
        },
      };
    }
    case SET_TAB.type: {
      return {
        ...state,
        tab: action.tab,
      };
    }
    case `${UPDATE_COMPARISON.type}_SUCCESS`: {
      // we need to test this one here as it it used both in the context of a comparison and from the list.

      if (!currentComparison && action.params.body.items != null) {
        return state;
      }

      const updatedComparison = action.data as Comparison;
      const updateList = (comparisons: ReducedComparison[], includeAssigned = true) => {
        let newList = replaceById(comparisons.slice(), updatedComparison, '_id') as ReducedComparison[];

        if (!includeAssigned) {
          // Filter any contact comparison - especially useful when saving drafts to contacts from
          // the drafts list.
          newList = newList.filter(comparison => comparison.contact === undefined);
        }

        return newList.sort((a, b) => (b.lastRunAt ? b.lastRunAt.getTime() : 0) - (a.lastRunAt ? a.lastRunAt.getTime() : 0));
      };
      const oldId = action.params.isNewlyAssigned ? action.params.id : undefined;
      const mergedActiveComparison = merge(currentComparison, updatedComparison);

      return {
        ...state,
        activeComparison: hasActiveComparison ? mergedActiveComparison : undefined,
        comparisons: {
          recent: {
            ...(state.comparisons as Dictionary<ComparisonsListState>).recent,
            list: updateList(state.comparisons.recent.list).filter(c => c._id !== oldId),
          },
          assigned: {
            ...(state.comparisons as Dictionary<ComparisonsListState>).assigned,
            list: updateList(state.comparisons.assigned.list).filter(c => c._id !== oldId),
          },
          drafts: {
            ...(state.comparisons as Dictionary<ComparisonsListState>).drafts,
            list: updateList(state.comparisons.drafts.list, false).filter(c => c._id !== oldId),
          },
          compliance: {
            ...(state.comparisons as Dictionary<ComparisonsListState>).compliance,
            list: updateList(state.comparisons.compliance.list, false).filter(c => c._id !== oldId),
          },
        },
      };
    }
    case SET_FORCED_MONTHLY_DATA.type: {
      return {
        ...state,
        forcedMonthlyData: action.forcedMonthlyData,
      };
    }
    case SET_ITEM_HOLDING.type: {
      const { itemId, holdingId, holdingData } = action;
      currentComparison.items = currentComparison.items.map(item => {
        if (item._id === itemId) {
          return {
            ...item,
            holdings: item.holdings.map(holding => {
              if (holding.id === holdingId) {
                return {
                  ...holding,
                  ...holdingData,
                };
              }

              return holding;
            }),
          };
        }

        return item;
      });
      currentComparison.isDirty = true;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case UPDATE_COMPARISON_TAX_SETTINGS.type: {
      currentComparison.taxAnalysis = {
        ...currentComparison.taxAnalysis,
        taxSettings: {
          ...currentComparison.taxAnalysis?.taxSettings,
          ...action.taxSettings,
        },
      };

      if (!action.avoidDirty) {
        currentComparison.isDirty = true;
      }

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SET_TAX_ANALYSIS_COMPARISON_ITEMS.type: {
      const updatedComparison = {
        ...currentComparison,
        taxAnalysis: {
          ...currentComparison.taxAnalysis,
          originalComparisonItemId: action.originalComparisonItemId,
          targetComparisonItemId: action.targetComparisonItemId,
        },
        isDirty: true,
      };

      // Clear all tax metrics when clearing the selection
      if (!action.originalComparisonItemId || !action.targetComparisonItemId) {
        updatedComparison.items = currentComparison.items.map(item => {
          if (!item.taxAnalysis) {
            return item;
          }

          const { taxAnalysis, ...cleanedItem } = item;

          if (item.holdings) {
            cleanedItem.holdings = item.holdings.map(holding => {
              const { taxMetrics, ...cleanedHolding } = holding;
              return cleanedHolding;
            });
          }
          return cleanedItem;
        });
      }

      return {
        ...state,
        activeComparison: updatedComparison,
      };
    }
    case SET_COMPARISON_ITEMS_FILTER.type: {
      return {
        ...state,
        itemsFilter: action.filter,
        activeComparison: currentComparison,
      };
    }
    case CHANGE_COMPARISON_ITEMS_ORDER.type: {
      const { itemIds } = action;
      const originalItems = [...currentComparison.items];
      currentComparison.items = itemIds.map((id: string) => currentComparison.items.find(i => i._id === id));

      // Update account sets to reflect the new item order
      if (currentComparison.accountSets && currentComparison.accountSets.length > 0) {
        currentComparison.accountSets = remapAccountSetIndices(currentComparison.accountSets, originalItems, currentComparison.items);
      }

      currentComparison.isDirty = true;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case CHANGE_ITEM_ID.type: {
      const { originalId, newId } = action;
      currentComparison.items = currentComparison.items.map(item => {
        if (item.type === COMPARISON_ITEM_TYPES.GROUP) {
          item.groupItems = item.groupItems?.map(groupItem => {
            if (groupItem.itemId === originalId) {
              return {
                ...groupItem,
                itemId: newId,
              };
            }

            return groupItem;
          });
        }

        if (item._id === originalId) {
          return {
            ...item,
            _id: newId,
          };
        }

        return item;
      });

      currentComparison.isDirty = true;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SET_COMPARISON_ITEM_FLAGS.type: {
      /**
       * This is almost the same as the SET_PROPOSAL_ITEM_FLAGS action in the
       * proposal slice, but currently needs to be repeated in the comparison
       * slice since they're separate and `toggleShortHistory` pulls from
       * comparison items when `refreshProposalItems` boolean is true.
       * Should probably be merged in a single action in the future.
       */
      const { itemId, flags } = action;
      const itemToChangeIndex: number = currentComparison.items.findIndex(i => i._id === itemId);

      const oldFlags = currentComparison.items[itemToChangeIndex].flags;
      const newFlags = flags;

      if (isEqual(oldFlags, newFlags)) {
        return state;
      }

      currentComparison.items = currentComparison.items.map((item, index) => (index === itemToChangeIndex ? { ...item, flags } : item));

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SET_ORIGIN_PATH.type: {
      return {
        ...state,
        originPath: action.originPath?.from,
      };
    }
    case SET_COMMON_INCEPTION_ITEM.type: {
      currentComparison.commonInception = action.commonInception;
      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SET_SHOULD_UPDATE_COMMON_INCEPTION.type: {
      return {
        ...state,
        shouldUpdateCommonInception: action.shouldUpdateCommonInception,
      };
    }
    case SET_PROPOSAL_LOCALE.type: {
      currentComparison.proposalLocale = action.locale;
      currentComparison.isDirty = true;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SET_SHOW_LOAD_CLIENT_PORTFOLIO_MODAL.type: {
      return {
        ...state,
        showLoadClientPortfoliosModal: action.showLoadClientPortfoliosModal,
      };
    }
    case UPDATE_COMPARISON_ACCOUNT_SETS.type: {
      currentComparison.accountSets = action.accountSets as AccountSet[];
      currentComparison.isDirty = true;

      return {
        ...state,
        activeComparison: currentComparison,
      };
    }
    case SET_SELECTED_HISTORICAL_SCENARIOS.type: {
      const isSelected = currentComparison.selectedHistoricalScenarios?.some(s => s._id === action.scenarioId);
      const updatedSelectedHistoricalScenarios = isSelected
        ? currentComparison.selectedHistoricalScenarios?.filter(s => s._id !== action.scenarioId)
        : [...(currentComparison.selectedHistoricalScenarios || []), action.scenario];

      return {
        ...state,
        activeComparison: {
          ...currentComparison,
          selectedHistoricalScenarios: updatedSelectedHistoricalScenarios,
          isDirty: action.avoidDirty ? currentComparison.isDirty : true,
        },
      };
    }
    case UPDATE_SELECTED_HISTORICAL_SCENARIO.type: {
      const updatedSelectedHistoricalScenarios =
        currentComparison.selectedHistoricalScenarios?.map(scenario => (scenario._id === action.scenario._id ? action.scenario : scenario)) || [];

      return {
        ...state,
        activeComparison: {
          ...currentComparison,
          selectedHistoricalScenarios: updatedSelectedHistoricalScenarios,
          isDirty: true,
        },
      };
    }
    case REMOVE_SELECTED_HISTORICAL_SCENARIO.type: {
      const updatedSelectedHistoricalScenarios = currentComparison.selectedHistoricalScenarios?.filter(scenario => scenario._id !== action.scenarioId) || [];

      return {
        ...state,
        activeComparison: {
          ...currentComparison,
          selectedHistoricalScenarios: updatedSelectedHistoricalScenarios,
          isDirty: true,
        },
      };
    }
    case SET_TAX_ANALYSIS_DATA.type: {
      const { comparisonItemId, taxMetrics } = action;

      // Update the specific comparison item with tax analysis data
      const updatedItems = currentComparison.items.map(item => {
        if (item._id !== comparisonItemId) {
          return item;
        }

        return {
          ...item,
          taxAnalysis: {
            ...item.taxAnalysis,
            taxMetrics,
          },
        };
      });

      return {
        ...state,
        activeComparison: {
          ...currentComparison,
          items: updatedItems,
        },
      };
    }

    case SET_TAX_ANALYSIS_HOLDINGS_DATA.type: {
      const { comparisonItemId, holdingsTaxMetrics } = action;

      // Update holdings with tax metrics for the specific comparison item
      const updatedItems = currentComparison.items.map(item => {
        if (item._id !== comparisonItemId) {
          return item;
        }

        // Update each holding with its corresponding tax metrics
        const updatedHoldings = item.holdings.map(holding => {
          const holdingTaxData = holdingsTaxMetrics.find((h: { holdingId: string }) => h.holdingId === holding.id);

          if (!holdingTaxData) {
            return holding;
          }

          return {
            ...holding,
            lots: holdingTaxData.lots,
            taxMetrics: holdingTaxData.taxMetrics,
          };
        });

        return {
          ...item,
          holdings: updatedHoldings,
        };
      });

      return {
        ...state,
        activeComparison: {
          ...currentComparison,
          items: updatedItems,
        },
      };
    }

    default: {
      return state;
    }
  }
}

export const openLoadClientPortfoliosModal = (): Result<void> => dispatch => {
  dispatch({ ...SET_SHOW_LOAD_CLIENT_PORTFOLIO_MODAL, showLoadClientPortfoliosModal: true });
};

export const closeLoadClientPortfoliosModal = (): Result<void> => dispatch => {
  dispatch({ ...SET_SHOW_LOAD_CLIENT_PORTFOLIO_MODAL, showLoadClientPortfoliosModal: false });
};

export const setTaxAnalysisData =
  (comparisonItemId: string, taxMetrics: PortfolioTaxMetrics): Result<void> =>
  dispatch => {
    dispatch({ ...SET_TAX_ANALYSIS_DATA, comparisonItemId, taxMetrics });
  };

export const setTaxAnalysisComparisonItems =
  (originalComparisonItemId?: string, targetComparisonItemId?: string): Result<void> =>
  dispatch => {
    dispatch({ ...SET_TAX_ANALYSIS_COMPARISON_ITEMS, originalComparisonItemId, targetComparisonItemId });
  };

export const setTaxAnalysisHoldingsData =
  (
    comparisonItemId: string,
    holdingsTaxMetrics: Array<{
      holdingId: string;
      taxMetrics: HoldingTaxMetrics;
    }>,
  ): Result<void> =>
  dispatch => {
    dispatch({ ...SET_TAX_ANALYSIS_HOLDINGS_DATA, comparisonItemId, holdingsTaxMetrics });
  };

export const updateAccountSets =
  (accountSets: AccountSet[]): Result<void> =>
  (dispatch, getState) => {
    const activeComparison = getCurrentStateComparison(getState().comparison);

    if (!activeComparison) {
      return;
    }

    dispatch({ ...UPDATE_COMPARISON_ACCOUNT_SETS, accountSets });
  };

// Action Creators

export const addNewPortfolioItemToComparison =
  (item: ComparisonItem): Result<void> =>
  dispatch => {
    dispatch({ ...ADD_ITEM, item });
    dispatch({ ...SET_SHOULD_UPDATE_COMMON_INCEPTION, shouldUpdateCommonInception: false });
    dispatch(logAddToComparison(item));
  };

export const addItemAndRecalculateComparison =
  (item: ComparisonItem): Result<void> =>
  dispatch => {
    dispatch({ ...ADD_ITEM, item });
    dispatch(setComparisonCorrelations());
    dispatch({ ...SET_SHOULD_UPDATE_COMMON_INCEPTION, shouldUpdateCommonInception: true });
    dispatch(logAddToComparison(item));
  };

export const replaceItemAndRecalculateComparison =
  (item: ComparisonItem): Result<void> =>
  dispatch => {
    dispatch({ ...REPLACE_ITEM, item });
    dispatch(setComparisonCorrelations());
    dispatch({ ...SET_SHOULD_UPDATE_COMMON_INCEPTION, shouldUpdateCommonInception: true });
  };

export const setChartOptions =
  (options: ComparisonChartOptions, chart: PERFORMANCE_CHART): Result<void> =>
  dispatch => {
    dispatch({ ...SET_CHART_OPTIONS, chart, options });
  };

export const setComparisonShouldForceMonthlyData =
  (force?: boolean): Result<void> =>
  (dispatch, getState) => {
    if (force != null) {
      dispatch({ ...SET_FORCED_MONTHLY_DATA, forcedMonthlyData: force });
      return;
    }

    const comparisonState = getState().comparison;
    const comparison = getCurrentStateComparison(getState().comparison);

    let useMonthly = comparison?.options?.growth?.useMonthly; // eslint-disable-line prefer-destructuring
    const isForced = comparisonState.forcedMonthlyData;

    if (useMonthly && !isForced) {
      // If we had manually toggled to use monthly data, we shouldn't automatically toggle it off.
      return;
    }

    if (!useMonthly && isForced) {
      // If we just manually toggled off the monthly data, set the forcedMonthlyData to be false too.
      dispatch({ ...SET_FORCED_MONTHLY_DATA, forcedMonthlyData: false });
      return;
    }

    useMonthly = comparison ? comparisonShouldForceMonthlyData(comparison) : false;

    dispatch(setChartOptions({ useMonthly }, PERFORMANCE_CHART.GROWTH));
    dispatch(setChartOptions({ useMonthly }, PERFORMANCE_CHART.DRAWDOWN));

    dispatch({ ...SET_FORCED_MONTHLY_DATA, forcedMonthlyData: useMonthly });
  };

const setItemsAndCalculateComparisonCorrelations =
  (items: ComparisonItem[]): Result<void> =>
  dispatch => {
    dispatch({ ...SET_ITEMS, items });
    dispatch(setComparisonShouldForceMonthlyData());
    dispatch(setComparisonCorrelations());
    dispatch({ ...SET_SHOULD_UPDATE_COMMON_INCEPTION, shouldUpdateCommonInception: true });
  };

export const addCategoryBenchmark =
  (item: ComparisonItem): AsyncResult<void> =>
  async (dispatch, getState) => {
    const state = getState();
    const {
      comparison: comparisonState,
      auth: { user },
      intl: { locale },
    } = state;

    const comparison = getCurrentStateComparison(comparisonState);
    if (!userHasPermission(user, PERMISSIONS.BENCHMARKS) || !comparison) {
      return Promise.resolve();
    }

    const forcedMonthlyRolling: boolean = shouldUseMonthlyRollingInReturns(comparison, state.auth.user);
    const benchmarkItem = await createCategoryBenchmarkFromItem(
      dispatch,
      item,
      comparison.items,
      locale,
      forcedMonthlyRolling,
      comparison.shortHistory,
      comparison.commonInception,
    );

    if (benchmarkItem) {
      const newItems = comparison.items.slice().concat(benchmarkItem);
      dispatch(setItemsAndCalculateComparisonCorrelations(newItems));
    }
  };

const addAlternativeModelsToComparison = async (dispatch: Dispatch, state: any, comparison: Comparison, alternativeModels: string[]): Promise<void> => {
  if (alternativeModels && alternativeModels.length > 0) {
    const enterprise = state.auth.user != null && state.auth.user.enterprise ? state.auth.user.enterprise : undefined;
    const portfolios = await Promise.all(
      alternativeModels.map(alternativeModel => dispatch(getPortfolioById(alternativeModel, 'FIRM', undefined, enterprise))),
    );
    const items = comparison.items.slice();
    await dispatch(addPortfoliosToComparison(portfolios.filter(portfolio => !items.find(item => portfolio._id === item._id))));
  }
};

const createCategoryBenchmarkFromItem = async (
  dispatch: Dispatch,
  item: ComparisonItem,
  currentItems: ComparisonItem[],
  locale: Locale,
  forcedMonthlyRolling: boolean,
  shortHistory?: boolean,
  commonInception?: CommonInception,
  portfolioData?: PortfolioData,
) => {
  try {
    let data;
    let portfolio;

    switch (item.type) {
      case COMPARISON_ITEM_TYPES.FUND:
        const fund = await dispatch(getFundData(item._id));
        data = {
          funds: [fund],
          stocks: [],
        };
        break;
      case COMPARISON_ITEM_TYPES.PORTFOLIO:
        portfolio = item.source || (await dispatch(getPortfolioById(item._id, item.portfolioType, item.contact, item.enterprise, undefined, item.source)));
        data = portfolioData || (await dispatch(getPortfolioHoldingsData(portfolio)));
        break;
      case COMPARISON_ITEM_TYPES.STOCK:
        const stock = await dispatch(getStockData(item._id));
        data = {
          funds: [],
          stocks: [stock],
        };
        break;
      default:
        throw new Error('Unsupported comparison item type');
    }

    const benchmarksMap = getCategoryBenchmarksMap(data, portfolio);
    const benchmarkItem = await getCategoryBenchmarkItem(dispatch, benchmarksMap, locale, forcedMonthlyRolling, shortHistory, portfolio, commonInception);

    if (benchmarkItem) {
      if (currentItems.some(i => i._id === benchmarkItem._id)) {
        return Promise.reject({ errorKey: 'BENCHMARK_ALREADY_ADDED' });
      }
      benchmarkItem.color = generateNewItemColor(currentItems);
    }

    return benchmarkItem;
  } catch (err) {
    LogRocketUtils.warn(err as any);
  }
};

const getCategoryBenchmarkItem = async (
  dispatch: Dispatch,
  benchmarksMap: Dictionary<number>,
  locale: Locale,
  forcedMonthlyRolling: boolean,
  shortHistory?: boolean,
  portfolio?: Portfolio,
  commonInception?: CommonInception,
) => {
  const benchmarkIds = Object.keys(benchmarksMap);

  const sum = Object.values(benchmarksMap).reduce((acc, curr) => acc + curr, 0);

  // If sum of the weights doesn't equal 100 we know a fund/stock is missing a benchmark id
  // Therefore, a partial benchmark should not be added to the comparison
  if (round(sum, 2) !== 100) {
    return Promise.reject(new Error('One or more funds/stocks is missing a benchmark id'));
  }

  if (benchmarkIds.length === 1 && locale) {
    const { benchmark, riskFree } = await dispatch(getComparisonBenchmarkData(benchmarkIds[0]));

    const benchmarkPortfolio = benchmarkToBenchmarkPortfolio(benchmark, locale);

    const data = [benchmark];
    if (riskFree) {
      data.push(riskFree);
    }
    const newBenchmarkPortfolio = await calculateBenchmarkPortfolioStats(
      dispatch,
      benchmarkPortfolio,
      data,
      forcedMonthlyRolling,
      shortHistory,
      commonInception,
    );
    return benchmarkToComparisonItem(newBenchmarkPortfolio, data, commonInception);
  }

  if (benchmarkIds.length > 1 && portfolio) {
    const categoryBenchmarks = await dispatch(getManyComparisonBenchmarkData(benchmarkIds));
    const benchmarkPortfolio = createPortfolioCategoryBenchmark(benchmarksMap, categoryBenchmarks, portfolio);
    const benchmarkData = await dispatch(getBenchmarksData(benchmarkPortfolio));
    const newBenchmarkPortfolio = await calculateBenchmarkPortfolioStats(
      dispatch,
      benchmarkPortfolio,
      benchmarkData,
      forcedMonthlyRolling,
      shortHistory,
      commonInception,
    );
    return benchmarkToComparisonItem(newBenchmarkPortfolio, benchmarkData, commonInception);
  }
};

export const selectCategory =
  (category: ComparisonCategory, availableFields: ComparisonField[], unselectedFields?: ComparisonField[]): Result<void> =>
  (dispatch, getState) => {
    const comparison = getCurrentStateComparison(getState().comparison);

    if (!comparison) {
      throw new Error('No current comparison');
    }

    const selectedCategories = { ...comparison.selectedCategories };

    if ((!unselectedFields || unselectedFields.length !== availableFields.length) && selectedCategories[category.key]) {
      selectedCategories[category.key].fields = [];

      dispatch({ ...SELECT_CATEGORY, selectedCategories });
      return;
    }

    const updatedSelectedCategories = Object.assign(selectedCategories, selectCategoryFields(category, availableFields));

    dispatch({ ...SELECT_CATEGORY, selectedCategories: updatedSelectedCategories });
  };

export const toggleCategory =
  (category: ComparisonCategory): Result<void> =>
  (dispatch, getState) => {
    const comparison = getCurrentStateComparison(getState().comparison);

    if (!comparison) {
      throw new Error('No current comparison');
    }

    let { toggledCategories } = comparison;

    if (toggledCategories.includes(category.key)) {
      toggledCategories = toggledCategories.filter(c => c !== category.key);
    } else {
      toggledCategories = [...toggledCategories, category.key];
    }

    dispatch({ ...SET_TOGGLED_CATEGORIES, toggledCategories });
  };

export const setSelectedHistoricalScenarios =
  (scenario: HistoricalScenario, avoidDirty = false): Result<void> =>
  (dispatch, getState) => {
    const comparison = getCurrentStateComparison(getState().comparison);

    if (!comparison) {
      throw new Error('No current comparison');
    }

    dispatch({ ...SET_SELECTED_HISTORICAL_SCENARIOS, scenarioId: scenario._id, scenario, avoidDirty });
  };

export const updateSelectedHistoricalScenario =
  (scenario: HistoricalScenario): Result<void> =>
  (dispatch, getState) => {
    const comparison = getCurrentStateComparison(getState().comparison);

    if (!comparison) {
      throw new Error('No current comparison');
    }

    // Only update if the scenario is currently selected
    const isSelected = comparison.selectedHistoricalScenarios?.some(s => s._id === scenario._id);
    if (isSelected) {
      dispatch({ ...UPDATE_SELECTED_HISTORICAL_SCENARIO, scenario });
    }
  };

export const removeSelectedHistoricalScenario =
  (scenarioId: string): Result<void> =>
  (dispatch, getState) => {
    const comparison = getCurrentStateComparison(getState().comparison);

    if (!comparison) {
      throw new Error('No current comparison');
    }

    // Only remove if the scenario is currently selected
    const isSelected = comparison.selectedHistoricalScenarios?.some(s => s._id === scenarioId);
    if (isSelected) {
      dispatch({ ...REMOVE_SELECTED_HISTORICAL_SCENARIO, scenarioId });
    }
  };

export const setExpandedFields =
  (fieldKey: string): Result<void> =>
  (dispatch, getState) => {
    const comparison = getCurrentStateComparison(getState().comparison);

    if (!comparison) {
      throw new Error('No current comparison');
    }

    const expandedFields = [...(comparison.expandedFields || [])];

    const fieldIndex = expandedFields.indexOf(fieldKey);
    if (fieldIndex >= 0) {
      expandedFields.splice(fieldIndex, 1);
    } else {
      expandedFields.push(fieldKey);
    }

    dispatch({ ...SET_EXPANDED_FIELDS, expandedFields });
  };

const updateDiffCategories = (
  dispatch: Dispatch,
  addedCategories: ComparisonCategory[],
  removedCategories: ComparisonCategory[],
  duplicateFields?: string[],
) => {
  for (const category of addedCategories) {
    const availableFields = getAvailableFields(category, duplicateFields);

    dispatch(selectCategory(category, availableFields, availableFields));
  }

  for (const category of removedCategories) {
    const availableFields = getAvailableFields(category, duplicateFields);

    dispatch(selectCategory(category, availableFields));
  }
};

export const toggleField =
  (category: ComparisonCategory, field: ComparisonField): Result<void> =>
  (dispatch, getState) => {
    const comparison = getCurrentStateComparison(getState().comparison);

    if (!comparison || !comparison.selectedCategories || !comparison.selectedCategories[category.key]) {
      return;
    }

    let newFields = comparison.selectedCategories[category.key].fields.slice();
    const isSelected = fieldIsSelected(field, category, comparison.selectedCategories);

    if (!isSelected) {
      newFields.push(field.key);
    } else {
      newFields = comparison.selectedCategories[category.key].fields.filter((f: string) => f !== field.key);
    }

    dispatch({ ...TOGGLE_FIELD, key: category.key, fields: newFields });
  };

const updateUnchangedCategories = (
  dispatch: Dispatch,
  unchangedCategories: ComparisonCategory[],
  previousCategories: ComparisonCategory[],
  currentCategories: ComparisonCategory[],
  oldSelectedCategories: SelectedCategories,
) => {
  for (const category of unchangedCategories) {
    // Compare old and new comparison category to see what fields have been changed
    const currentCategory = currentCategories.slice().find(cat => cat.key === category.key);
    const previousCategory = previousCategories.slice().find(cat => cat.key === category.key);

    if (currentCategory && previousCategory) {
      const addedFields = diffArray(currentCategory.fields, previousCategory.fields, 'key');
      const removedFields = diffArray(previousCategory.fields, currentCategory.fields, 'key');
      const oldSelectedFields = oldSelectedCategories[currentCategory.key].fields;

      for (const field of addedFields) {
        // Dont toggle disabled mer fields (total cost, advisory fee) if MER was not selected previously
        if (
          DISABLED_MER_FIELDS.includes(field.key) &&
          previousCategory.fields.some(f => f.key === MER_FIELDS.MER) &&
          !oldSelectedFields.includes(MER_FIELDS.MER)
        ) {
          continue;
        }

        // Only toggle if it was not previously selected
        if (!oldSelectedFields.includes(field.key)) {
          dispatch(toggleField(currentCategory, field));
        }
      }

      for (const field of removedFields) {
        // Only toggle if it was previously selected
        if (oldSelectedFields.includes(field.key)) {
          dispatch(toggleField(currentCategory, field));
        }
      }
    }
  }
};

export const getSharedByUserInfos = (): AsyncResult<BasicUserInfos | undefined> => (dispatch, getState) => {
  const comparison = getCurrentStateComparison(getState().comparison);
  if (!comparison || !comparison.sharedBy) {
    return Promise.resolve(undefined);
  }

  return dispatch(getUserInfos(comparison.sharedBy)).then(userInfos => {
    dispatch({ ...SET_SHARED_BY_USER_INFOS, userInfos });
    return userInfos;
  });
};

export const unselectAllComparisonItems = (): Result<void> => dispatch => {
  dispatch({ type: UNSELECT_ALL_COMPARISON_ITEMS.type });
};

export const loadComparison =
  (comparison: Comparison): AsyncResult<Comparison> =>
  (dispatch, getState) => {
    dispatch(startLoaderAction(LOAD_COMPARISON.type));

    const state = getState();
    const currentComparisonId = state.comparison.activeComparison?._id;
    let data = deepClone(comparison) as Comparison;
    data.commonInception = getCommonInception(data, state.comparison.itemsFilter, comparison.shortHistory);

    return new Promise(async resolve => {
      dispatch({ ...SET_IS_LOADING, isLoading: true, ...(comparison.monthEndDataOption && { monthEndDataOption: comparison.monthEndDataOption }) });

      if (!comparisonIsReadOnly(comparison)) {
        // handle autoMonthEndData
        if (data.monthEndDataOption === PERIOD_END_DATA_OPTIONS.AUTO && state.auth.user?.preferences?.autoPerformanceAndRiskData) {
          data.monthEndDataOption = state.auth.user?.preferences?.autoPerformanceAndRiskData as PERIOD_END_DATA_OPTIONS;
          data.autoMonthEndData = true;
        }

        // Refresh the entire comparison
        // New draft comparison doesn't have item at this point, doesn't need to be refreshed
        if (data.items.length > 0) {
          data = await refreshComparisonData(dispatch, state, data);
        }

        // Update startDate and endDate for all dated charts if present
        if (data.options) {
          data.options = updateChartDatesToToday(data.options, data.monthEndDataOption);
          dispatch(setChartOptions({ startDate: data.options.growth?.startDate, endDate: data.options.growth?.endDate }, PERFORMANCE_CHART.GROWTH));
          dispatch(
            setChartOptions({ startDate: data.options.fundGrowth?.startDate, endDate: data.options.fundGrowth?.endDate }, PERFORMANCE_CHART.FUND_GROWTH),
          );
          dispatch(
            setChartOptions(
              { startDate: data.options.portfolioGrowth?.startDate, endDate: data.options.portfolioGrowth?.endDate },
              PERFORMANCE_CHART.PORTFOLIO_GROWTH,
            ),
          );
          dispatch(
            setChartOptions({ startDate: data.options.groupGrowth?.startDate, endDate: data.options.groupGrowth?.endDate }, PERFORMANCE_CHART.GROUP_GROWTH),
          );
          dispatch(setChartOptions({ startDate: data.options.drawdown?.startDate, endDate: data.options.drawdown?.endDate }, PERFORMANCE_CHART.DRAWDOWN));
        }

        if (comparisonHasMissingOrModifiedItems(data)) {
          dispatch({ type: LOAD_COMPARISON.type, comparison: data });
          dispatch({ ...SET_SHOULD_UPDATE_COMMON_INCEPTION, shouldUpdateCommonInception: true });
          dispatch(stopLoaderAction(LOAD_COMPARISON.type));
          return resolve(data);
        }
      } else {
        // Refresh just the holdings from the product groups
        data = refreshComparisonGroupItemsHoldings(data);
      }

      if (comparison._id !== currentComparisonId) {
        dispatch(unselectAllComparisonItems());
      }

      dispatch({ type: LOAD_COMPARISON.type, comparison: data });
      dispatch({ ...SET_SHOULD_UPDATE_COMMON_INCEPTION, shouldUpdateCommonInception: true });

      // Auto-select 3 most recent historical scenarios if none are selected
      if (userHasPermission(state.auth.user, PERMISSIONS.HISTORICAL_SCENARIOS)) {
        const existingSelectedScenarios = data.selectedHistoricalScenarios || [];
        if (existingSelectedScenarios.length === 0) {
          const { data: historicalScenariosData } = await dispatch(historicalScenariosApi.endpoints.getAvailableScenarios.initiate());

          if (historicalScenariosData?.predefinedScenarios && historicalScenariosData?.predefinedScenarios.length > 0) {
            const mostRecent = [...historicalScenariosData.predefinedScenarios]
              .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
              .slice(0, 3);

            mostRecent.forEach(scenario => {
              dispatch(setSelectedHistoricalScenarios(scenario, true));
            });
          }
        }
      }

      // Dispatch the action to get the user infos, but no need to wait for it - they'll get displayed them when they get loaded.
      if (comparison.sharedBy) {
        dispatch(getSharedByUserInfos());
      }

      dispatch(setComparisonCorrelations());
      dispatch(stopLoaderAction(LOAD_COMPARISON.type));

      return resolve(data);
    });
  };

export const addAltToComparisonById =
  (id: string): AsyncResult<void> =>
  async (dispatch, getState) => {
    dispatch(startLoaderAction(ADD_ALT_TO_COMPARISON.type, { comparisonById: id }));

    const {
      auth: { user },
      comparison: { activeComparison },
      contacts,
    } = getState();
    const contact = contacts.current?.contact;
    let comparison = activeComparison;

    if (!comparison || comparisonIsReadOnly(comparison)) {
      comparison = generateDraftComparison(user, contact);
      dispatch(loadComparison(comparison));
    }

    const items = [...comparison.items];
    if (items.some(item => item._id === id)) {
      dispatch(stopLoaderAction(ADD_ALT_TO_COMPARISON.type));
      return Promise.reject({ errorKey: 'ALT_ALREADY_ADDED' });
    }

    const alt = await dispatch(getAltData(id));
    if (!alt || !alt._id) {
      dispatch(stopLoaderAction(ADD_ALT_TO_COMPARISON.type));
      return Promise.reject({ errorKey: 'ALT_NOT_FOUND' });
    }

    const options = { comparison };
    const item = altToComparisonItem(alt, options);
    item.color = generateNewItemColor(items);
    items.push(item);

    if (comparison._id && !comparison._id.startsWith('new-')) {
      item.sharedFrom = comparison._id;
    }

    dispatch(setItemsAndCalculateComparisonCorrelations(items));
    dispatch(logAddToComparison(item));
    dispatch(stopLoaderAction(ADD_ALT_TO_COMPARISON.type));
  };

export const addFundsToComparison =
  (funds: Fund[]): AsyncResult<void> =>
  async (dispatch, getState) => {
    dispatch(startLoaderAction(ADD_FUNDS_TO_COMPARISON.type));
    const state = getState();
    const {
      comparison: comparisonState,
      auth: { user },
      intl: { locale },
      contacts,
    } = state;
    const contact = contacts.current != null ? contacts.current.contact : undefined;
    let comparison = getCurrentStateComparison(comparisonState);

    if (!comparison || comparisonIsReadOnly(comparison)) {
      comparison = generateDraftComparison(user, contact);
      await dispatch(loadComparison(comparison));
    }

    const items = comparison.items.slice();

    const newFunds = funds.filter(f => !items.some(i => i._id === f._id));
    await dispatch(cacheFundsData(newFunds));
    const benchmarks = await dispatch(getFundsBenchmarks(newFunds));

    const appRegion: AppRegion = state.auth.user != null ? state.auth.user.appConfig.appRegion : (AppRegion.CAN as AppRegion);
    const options = {
      comparison,
    };

    for (const fund of newFunds) {
      const item = fundToComparisonItem(fund, appRegion, benchmarks, options);
      item.color = generateNewItemColor(items);

      if (comparison._id && !comparison._id.startsWith('new-')) {
        item.sharedFrom = comparison._id;
      }

      dispatch(addSearchItemLog(fund._id, COMPARISON_ITEM_TYPES.FUND, SEARCH_ITEM_SOURCE.ADVANCED_SEARCH, SEARCH_ITEM_DEST.COMPARISON));
      dispatch(logAddToComparison(item));

      items.push(item);

      // Auto add category benchmark of first fund
      if (shouldAutoAddBenchmark(user) && items.length === 1) {
        const forcedMonthlyRolling: boolean = comparison != null ? shouldUseMonthlyRollingInReturns(comparison, getState().auth.user) : false;
        const newBenchmark = await createCategoryBenchmarkFromItem(
          dispatch,
          item,
          items,
          locale,
          forcedMonthlyRolling,
          comparison.shortHistory,
          comparison.commonInception,
        );
        if (newBenchmark) {
          items.push(newBenchmark);
        }
      }
    }
    dispatch(setItemsAndCalculateComparisonCorrelations(items));
    dispatch(stopLoaderAction(ADD_FUNDS_TO_COMPARISON.type));
  };

export const addPortfoliosToComparison =
  (portfolios: Portfolio[]): AsyncResult<void> =>
  async (dispatch, getState) => {
    if (portfolios == null || portfolios.length === 0) {
      return Promise.resolve();
    }

    dispatch(startLoaderAction(ADD_PORTFOLIOS_TO_COMPARISON.type, { portfolios }));
    const state = getState();
    const {
      comparison: comparisonState,
      auth: { user },
      intl: { locale },
      contacts,
      households: { currentHousehold },
    } = state;
    const contact = contacts.current != null ? contacts.current.contact : undefined;
    const appRegion: AppRegion = state.auth.user != null ? state.auth.user.appConfig.appRegion : (AppRegion.CAN as AppRegion);
    let currentComparison = getCurrentStateComparison(comparisonState);
    if (!currentComparison || comparisonIsReadOnly(currentComparison)) {
      currentComparison = generateDraftComparison(user, contact);
      if (currentHousehold && !contacts.current?.contact) {
        currentComparison.householdId = currentHousehold.id;
        currentComparison.householdName = currentHousehold.name;
        currentComparison.name = formatMessage('Comparison.NewForHousehold', { name: currentHousehold.name || '' });
        currentComparison.contacts = currentHousehold.contacts.map(createComparisonContact);
        currentComparison.comparisonType = COMPARISON_TYPE.CONTACT;
      }
      dispatch(loadComparison(currentComparison));
    }
    const comparison = currentComparison;

    const items = comparison.items.slice();
    if (items.some(item => portfolios.some(portfolio => portfolio._id === item._id))) {
      dispatch(stopLoaderAction(ADD_PORTFOLIOS_TO_COMPARISON.type));
      return Promise.reject({ errorKey: 'PORTFOLIO_ALREADY_ADDED' });
    }

    const validateIncomeStream = state.auth.user != null && userHasPermission(state.auth.user, PERMISSIONS.INCOME_STREAM);
    const forcedMonthlyRolling: boolean = shouldUseMonthlyRollingInReturns(comparison, state.auth.user);

    const promises = portfolios
      .filter(p => !items.some(i => i._id === p._id))
      .map(async (portfolio, index) => {
        const data = await dispatch(getPortfolioHoldingsData(portfolio));
        const newPortfolio = reallocateAndRecalculatePortfolio(portfolio, data, forcedMonthlyRolling, 0, comparison.commonInception);
        const taxRate = selectCurrentTaxRate(state, newPortfolio.contact);
        const options = {
          taxRate,
          shortHistory: comparison.shortHistory,
          comparison,
        };
        let comparisonItem = portfolioToComparisonItem(newPortfolio, appRegion, validateIncomeStream, data, options);
        if (comparison._id && !comparison._id.startsWith('new-')) {
          comparisonItem.sharedFrom = comparison._id;
        }

        comparisonItem = await recalcPortfolioItem(
          dispatch,
          state,
          comparisonItem,
          forcedMonthlyRolling,
          comparison.shortHistory,
          comparison.showBaseCurrencies,
          true,
          comparison.commonInception,
        );

        comparisonItem.color = generateNewItemColor(items);
        items.push(comparisonItem);

        // Auto add category benchmark of first portfolio, only when creating a new comparison
        if (shouldAutoAddBenchmark(user) && index === 0 && comparisonItem._id === items[0]._id) {
          const newBenchmark = await createCategoryBenchmarkFromItem(
            dispatch,
            comparisonItem,
            items,
            locale,
            forcedMonthlyRolling,
            comparison.shortHistory,
            undefined,
            data,
          );
          if (newBenchmark) {
            items.push(newBenchmark);
          }
        }

        return comparisonItem;
      });

    // TODO -- Change the return value, promises should return an Error. It's hard to catch promises cleanly when the error type is unexpected.
    // Currently specific to addPortfoliosToComparison only
    const handleError = (err: Error | { errorKey?: string; data?: { errorKey?: string } }) => {
      let key: string | undefined;

      if (!(err instanceof Error)) {
        key = err.errorKey || (err.data && err.data.errorKey);
      }

      if (!key && err instanceof Error) {
        sentryUtils.captureError(err);
      }

      dispatch(stopLoaderAction(ADD_PORTFOLIOS_TO_COMPARISON.type));
      return Promise.reject({ errorKey: key || 'DEFAULT_ERROR' });
    };

    // Dispatch the new items only once all the rolling performance data has been populated
    return Promise.all(promises)
      .then(async newItems => {
        const comparison = getCurrentStateComparison(getState().comparison);

        if (!comparison) {
          throw new Error('No current comparison');
        }

        dispatch(setItemsAndCalculateComparisonCorrelations(items));

        // Add event to usage logs
        dispatch(logAddToComparison(...newItems));

        // Clear any remote suitability results that may have been saved whenever a portfolio is added to a comparison
        dispatch(clearSuitabilityAnalysis());
      })
      .catch(handleError)
      .finally(() => {
        dispatch(stopLoaderAction(ADD_PORTFOLIOS_TO_COMPARISON.type));
        if (dom.appLoaderIsVisible()) {
          dom.removeAppLoader();
        }
      });
  };

export const addFundToComparisonById =
  (id: string): AsyncResult<void> =>
  async (dispatch, getState) => {
    dispatch(startLoaderAction(ADD_FUND_TO_COMPARISON.type, { comparisonId: id }));

    const state = getState();
    const {
      comparison: comparisonState,
      auth: { user },
      intl: { locale },
      contacts,
    } = state;
    const contact = contacts.current != null ? contacts.current.contact : undefined;
    let comparison = getCurrentStateComparison(comparisonState);

    if (!comparison || comparisonIsReadOnly(comparison)) {
      comparison = generateDraftComparison(user, contact);
      await dispatch(loadComparison(comparison));
    }

    let items = comparison.items.slice();
    if (items.some(item => item._id === id)) {
      dispatch(stopLoaderAction(ADD_FUND_TO_COMPARISON.type));
      return Promise.reject({ errorKey: 'FUND_ALREADY_ADDED' });
    }

    let fund;
    try {
      fund = await dispatch(getFundData(id));
    } catch (error) {
      dispatch(stopLoaderAction(ADD_FUND_TO_COMPARISON.type));
      throw new Error(`${id.startsWith(BOND_TAG) ? 'Bond' : 'Fund'} not found`);
    }

    const benchmarks = await dispatch(getFundBenchmarks(fund));

    // re-assigning comparison and items so they are synchronous and each get a unique colour
    comparison = getCurrentStateComparison(getState().comparison);
    if (!comparison) {
      dispatch(stopLoaderAction(ADD_FUND_TO_COMPARISON.type));
      return;
    }

    items = comparison.items;

    const appRegion: AppRegion = state.auth.user != null ? state.auth.user.appConfig.appRegion : (AppRegion.CAN as AppRegion);
    const options = {
      comparison,
    };
    const item = fundToComparisonItem(fund, appRegion, benchmarks, options);
    item.color = generateNewItemColor(items);
    items.push(item);

    if (comparison._id && !comparison._id.startsWith('new-')) {
      item.sharedFrom = comparison._id;
    }

    const newItems = [item];

    // Auto add category benchmark
    if (shouldAutoAddBenchmark(user) && items.length === 1) {
      const forcedMonthlyRolling: boolean = shouldUseMonthlyRollingInReturns(comparison, state.auth.user);
      const newBenchmark = await createCategoryBenchmarkFromItem(
        dispatch,
        item,
        items,
        locale,
        forcedMonthlyRolling,
        comparison.shortHistory,
        comparison.commonInception,
      );
      if (newBenchmark) {
        items.push(newBenchmark);
        newItems.push(newBenchmark);
      }
    }

    dispatch(logAddToComparison(...newItems));
    dispatch(setItemsAndCalculateComparisonCorrelations(items));
    dispatch(stopLoaderAction(ADD_FUND_TO_COMPARISON.type));
  };

export const addFundsToComparisonById =
  (ids: string[]): AsyncResult<void[]> =>
  dispatch => {
    dispatch(startLoaderAction(ADD_FUNDS_TO_COMPARISON.type));
    const promises = ids.map(id => dispatch(addFundToComparisonById(id)));
    return Promise.all(promises).finally(() => {
      dispatch(stopLoaderAction(ADD_FUNDS_TO_COMPARISON.type));
    });
  };

export const addBenchmarkToComparisonById =
  (id: string): AsyncResult<void> =>
  (dispatch, getState) => {
    dispatch(startLoaderAction(ADD_BENCHMARK_TO_COMPARISON.type, { comparisonId: id }));

    const state = getState();
    const {
      auth: { user },
      intl: { locale },
      comparison: comparisonState,
      contacts,
    } = state;
    const contact = contacts.current != null ? contacts.current.contact : undefined;

    let currentComparison = getCurrentStateComparison(comparisonState);

    if (!currentComparison || comparisonIsReadOnly(currentComparison)) {
      currentComparison = generateDraftComparison(user, contact);
      dispatch(loadComparison(currentComparison));
    }

    const comparison = currentComparison;

    const benchmarks = comparison.items.slice().filter(item => item.type === COMPARISON_ITEM_TYPES.BENCHMARK);
    for (const b of benchmarks) {
      const { holdings } = b;
      if (holdings.length === 1 && holdings[0].id === id) {
        dispatch(stopLoaderAction(ADD_BENCHMARK_TO_COMPARISON.type));
        return Promise.reject({ errorKey: 'BENCHMARK_ALREADY_ADDED' });
      }
    }

    return dispatch(getComparisonBenchmarkData(id))
      .then(async ({ benchmark, riskFree }) => {
        const benchmarkPortfolio = benchmarkToBenchmarkPortfolio(benchmark, locale);
        const data = [benchmark];
        if (riskFree) {
          data.push(riskFree);
        }
        const forcedMonthlyRolling: boolean = comparison != null ? shouldUseMonthlyRollingInReturns(comparison, getState().auth.user) : false;
        const newBenchmarkPortfolio = await calculateBenchmarkPortfolioStats(
          dispatch,
          benchmarkPortfolio,
          data,
          forcedMonthlyRolling,
          comparison.shortHistory,
          comparison.commonInception,
        );
        const item = benchmarkToComparisonItem(newBenchmarkPortfolio, data, comparison?.commonInception);
        item.color = generateNewItemColor(comparison.items.slice());

        if (comparison._id && !comparison._id.startsWith('new-')) {
          item.sharedFrom = comparison._id;
        }

        dispatch(addItemAndRecalculateComparison(item));
      })
      .finally(() => dispatch(stopLoaderAction(ADD_BENCHMARK_TO_COMPARISON.type)));
  };

export const addBenchmarksToComparison =
  (benchmarks: ComparisonBenchmark[]): AsyncResult<void> =>
  async (dispatch, getState) => {
    const {
      auth: { user },
      comparison: comparisonState,
      contacts,
    } = getState();
    const contact = contacts.current != null ? contacts.current.contact : undefined;

    let comparison = getCurrentStateComparison(comparisonState);

    if (!comparison || comparisonIsReadOnly(comparison)) {
      comparison = generateDraftComparison(user, contact);
      await dispatch(loadComparison(comparison));
    }

    const items = comparison.items.slice();

    const newBenchmarks = benchmarks.filter(b => !items.some(i => i._id === b._id));

    for (const benchmark of newBenchmarks) {
      const benchmarkPortfolio = {
        _id: benchmark._id,
        name: formatLocalizedString(benchmark.name),
        holdings: [
          {
            type: COMPARISON_ITEM_TYPES.BENCHMARK,
            id: benchmark._id,
            name: benchmark.name || '',
            symbol: benchmark.ticker || '',
            weight: 100,
            currency: benchmark.currency || '',
            riskFreeId: benchmark.riskFreeId,
          },
        ],
        dates: {},
      } as BenchmarkPortfolio;

      const data = [benchmark];
      if (benchmark.riskFreeId) {
        const riskFree = await dispatch(getComparisonBenchmark(benchmark.riskFreeId));
        if (riskFree) {
          data.push(riskFree);
        }
      }

      const forcedMonthlyRolling: boolean = comparison != null ? shouldUseMonthlyRollingInReturns(comparison, getState().auth.user) : false;
      const newBenchmarkPortfolio = await calculateBenchmarkPortfolioStats(
        dispatch,
        benchmarkPortfolio,
        data,
        forcedMonthlyRolling,
        comparison?.shortHistory,
        comparison?.commonInception,
      );
      const item = benchmarkToComparisonItem(newBenchmarkPortfolio, data, comparison.commonInception);
      item.color = generateNewItemColor(items);

      if (comparison._id && !comparison._id.startsWith('new-')) {
        item.sharedFrom = comparison._id;
      }

      items.push(item);

      dispatch(logAddToComparison(item));
    }

    dispatch(setItemsAndCalculateComparisonCorrelations(items));
  };

export const addStockToComparisonById =
  (id: string): AsyncResult<void> =>
  async (dispatch, getState) => {
    dispatch(startLoaderAction(ADD_STOCK_TO_COMPARISON.type, { comparisonById: id }));

    const state = getState();
    const {
      comparison: comparisonState,
      auth: { user },
      intl: { locale },
      contacts,
    } = state;
    const contact = contacts.current != null ? contacts.current.contact : undefined;
    let comparison = getCurrentStateComparison(comparisonState);

    if (!comparison || comparisonIsReadOnly(comparison)) {
      comparison = generateDraftComparison(user, contact);
      dispatch(loadComparison(comparison));
    }

    const items = comparison.items.slice();
    if (items.some(item => item._id === id)) {
      dispatch(stopLoaderAction(ADD_STOCK_TO_COMPARISON.type));
      return Promise.reject({ errorKey: 'STOCK_ALREADY_ADDED' });
    }

    const stock = await dispatch(getStockData(id));
    const benchmarks = await dispatch(getStockBenchmarks(stock));
    const options = {
      comparison,
    };

    const appRegion: AppRegion = state.auth.user != null ? state.auth.user.appConfig.appRegion : (AppRegion.CAN as AppRegion);
    const item = stockToComparisonItem(stock, benchmarks.map(comparisonBenchmarkToBenchmark), options, appRegion);
    item.color = generateNewItemColor(items);
    items.push(item);

    if (comparison._id && !comparison._id.startsWith('new-')) {
      item.sharedFrom = comparison._id;
    }

    const newItems = [item];

    // Auto add category benchmark
    if (userHasPermission(state.auth.user, PERMISSIONS.COMPARISON_AUTO_BENCHMARK) && items.length === 1) {
      const forcedMonthlyRolling: boolean = shouldUseMonthlyRollingInReturns(comparison, state.auth.user);
      const benchmarkItem = await createCategoryBenchmarkFromItem(
        dispatch,
        item,
        comparison.items,
        locale,
        forcedMonthlyRolling,
        comparison.shortHistory,
        comparison.commonInception,
      );

      if (benchmarkItem) {
        items.push(benchmarkItem);
        newItems.push(benchmarkItem);
      }
    }

    dispatch(setItemsAndCalculateComparisonCorrelations(items));
    dispatch(logAddToComparison(...newItems));
    dispatch(stopLoaderAction(ADD_STOCK_TO_COMPARISON.type));
  };

export const addPortfolioToComparisonById =
  (quickSearchItem: QuickSearchItem): AsyncResult<void> =>
  async (dispatch, getState) => {
    dispatch(startLoaderAction(ADD_PORTFOLIO_TO_COMPARISON.type, { quickSearchItem }));

    const state = getState();
    const {
      comparison: comparisonState,
      auth: { user },
      intl: { locale },
      contacts,
    } = state;
    const contact = contacts.current != null ? contacts.current.contact : undefined;
    const appRegion: AppRegion = state.auth.user != null ? state.auth.user.appConfig.appRegion : (AppRegion.CAN as AppRegion);
    let comparison = getCurrentStateComparison(comparisonState);

    if (!comparison || comparisonIsReadOnly(comparison)) {
      comparison = generateDraftComparison(user, contact);
      await dispatch(loadComparison(comparison));
    }

    const items = comparison.items.slice();
    if (items.some(item => item._id === quickSearchItem._id)) {
      dispatch(stopLoaderAction(ADD_PORTFOLIO_TO_COMPARISON.type));
      return Promise.reject({ errorKey: 'PORTFOLIO_ALREADY_ADDED' });
    }

    const portfolio = await dispatch(getPortfolioById(quickSearchItem._id, quickSearchItem.portfolioType, undefined, quickSearchItem.enterprise)).finally(() =>
      dispatch(stopLoaderAction(ADD_PORTFOLIO_TO_COMPARISON.type)),
    );

    const forcedMonthlyRolling: boolean = shouldUseMonthlyRollingInReturns(comparison, state.auth.user);

    const data = await dispatch(getPortfolioHoldingsData(portfolio));
    const taxRate = selectCurrentTaxRate(state, portfolio.contact);
    const validateIncomeStream = state.auth.user != null && userHasPermission(state.auth.user, PERMISSIONS.INCOME_STREAM);
    const newPortfolio = reallocateAndRecalculatePortfolio(portfolio, data, forcedMonthlyRolling, taxRate, comparison.commonInception);

    const options = {
      taxRate,
      shortHistory: comparison.shortHistory,
      comparison,
    };
    let item = portfolioToComparisonItem(newPortfolio, appRegion, validateIncomeStream, data, options);
    item.color = generateNewItemColor(items);
    item = await recalcPortfolioItem(
      dispatch,
      state,
      item,
      forcedMonthlyRolling,
      comparison.shortHistory,
      comparison.showBaseCurrencies,
      true,
      comparison.commonInception,
    );
    items.push(item);

    if (comparison._id && !comparison._id.startsWith('new-')) {
      item.sharedFrom = comparison._id;
    }

    const newItems = [item];

    // Auto add category benchmark
    if (userHasPermission(state.auth.user, PERMISSIONS.COMPARISON_AUTO_BENCHMARK) && items.length === 1) {
      const benchmarkItem = await createCategoryBenchmarkFromItem(
        dispatch,
        item,
        comparison.items,
        locale,
        forcedMonthlyRolling,
        comparison.shortHistory,
        comparison.commonInception,
        data,
      );

      if (benchmarkItem) {
        items.push(benchmarkItem);
        newItems.push(benchmarkItem);
      }
    }

    dispatch(setItemsAndCalculateComparisonCorrelations(items));
    dispatch(logAddToComparison(...newItems));
    dispatch(stopLoaderAction(ADD_PORTFOLIO_TO_COMPARISON.type));
  };

export const addBenchmarkPortfolioToComparisonById =
  (id: string): AsyncResult<void> =>
  async (dispatch, getState) => {
    dispatch(startLoaderAction(ADD_BENCHMARK_PORTFOLIOS_TO_COMPARISON.type, { portfolioId: id }));
    let comparison = getCurrentStateComparison(getState().comparison);

    const {
      auth: { user },
      contacts,
    } = getState();
    const contact = contacts.current != null ? contacts.current.contact : undefined;

    if (!comparison || comparisonIsReadOnly(comparison)) {
      comparison = generateDraftComparison(user, contact);
      await dispatch(loadComparison(comparison));
    }

    const items = comparison.items.slice();
    if (items.some(item => item._id === id)) {
      dispatch(stopLoaderAction(ADD_BENCHMARK_PORTFOLIOS_TO_COMPARISON.type));
      return Promise.reject({ errorKey: 'BENCHMARK_ALREADY_ADDED' });
    }

    const portfolio = await dispatch(getBenchmarkPortfolioById(id));
    const data = await dispatch(getBenchmarksData(portfolio));
    const item = benchmarkToComparisonItem(portfolio, data, comparison.commonInception);
    item.color = generateNewItemColor(items);

    if (comparison._id && !comparison._id.startsWith('new-')) {
      item.sharedFrom = comparison._id;
    }

    dispatch(stopLoaderAction(ADD_BENCHMARK_PORTFOLIOS_TO_COMPARISON.type));
    dispatch(addItemAndRecalculateComparison(item));
  };

export const addBenchmarkPortfoliosToNewComparison =
  (benchmarkPortfolios: BenchmarkPortfolio[]): AsyncResult<void> =>
  async (dispatch, getState) => {
    dispatch(startLoaderAction(ADD_BENCHMARK_PORTFOLIOS_TO_COMPARISON.type, { benchmarkPortfolios }));
    dispatch(startLoaderAction(ADD_PORTFOLIOS_TO_COMPARISON.type, { benchmarkPortfolios }));
    const state = getState();
    const {
      auth: { user },
      contacts,
    } = state;
    const contact = contacts.current != null ? contacts.current.contact : undefined;
    const comparison = generateDraftComparison(user, contact);
    await dispatch(loadComparison(comparison));

    const items: ComparisonItem[] = [];
    const promises = benchmarkPortfolios
      .filter(p => !items.some(i => i._id === p._id))
      .map(async portfolio => {
        const data = await dispatch(getBenchmarksData(portfolio));
        let comparisonItem = benchmarkToComparisonItem(portfolio, data, comparison?.commonInception);

        if (comparison._id && !comparison._id.startsWith('new-')) {
          comparisonItem.sharedFrom = comparison._id;
        }

        const forcedMonthlyRolling: boolean = shouldUseMonthlyRollingInReturns(comparison, state.auth.user);
        comparisonItem = await recalcBenchmarkItem(dispatch, comparisonItem, forcedMonthlyRolling, comparison, comparison.shortHistory);
        return comparisonItem;
      });

    // TODO -- Change the return value, promises should return an Error. It's hard to catch promises cleanly when the error type is unexpected.
    // Currently specific to addPortfoliosToComparison only
    const handleError = (err: Error | { errorKey?: string; data?: { errorKey?: string } }) => {
      let key: string | undefined;

      if (!(err instanceof Error)) {
        key = err.errorKey || (err.data && err.data.errorKey);
      }

      if (!key && err instanceof Error) {
        sentryUtils.captureError(err);
      }

      dispatch(stopLoaderAction(ADD_PORTFOLIOS_TO_COMPARISON.type));
      return Promise.reject({ errorKey: key || 'DEFAULT_ERROR' });
    };

    // Dispatch the new items only once all the rolling performance data has been populated
    return Promise.all(promises)
      .then(newItems => {
        newItems.forEach(item => {
          item.color = generateNewItemColor(items);
          items.push(item);
        });

        const comparison = getCurrentStateComparison(getState().comparison);
        if (!comparison) {
          throw new Error('No current comparison');
        }
        dispatch(setItemsAndCalculateComparisonCorrelations(items));

        // Clear any remote suitability results that may have been saved whenever a portfolio is added to a comparison
        dispatch(clearSuitabilityAnalysis());
      })
      .catch(handleError)
      .finally(() => {
        dispatch(stopLoaderAction(ADD_BENCHMARK_PORTFOLIOS_TO_COMPARISON.type));
        dispatch(stopLoaderAction(ADD_PORTFOLIOS_TO_COMPARISON.type));
      });
  };

export const clonePortfolio =
  (portfolio: Portfolio): AsyncResult<void> =>
  async (dispatch, getState) => {
    const state = getState();
    const appRegion: AppRegion = state.auth.user != null ? state.auth.user.appConfig.appRegion : (AppRegion.CAN as AppRegion);
    const comparison = getCurrentStateComparison(state.comparison);

    if (!comparison) {
      return Promise.reject(new Error('No current comparison'));
    }

    const items = comparison.items.slice();
    const portfolioClone = createPortfolioClone(portfolio);

    const clonedItem = items.find(item => item._id === portfolioClone._id);
    // Remove contact / enterprise infos and "mine" property
    delete portfolioClone.mine;
    delete portfolioClone.enterprise;
    delete portfolioClone.enterpriseGroups;
    delete portfolioClone.contact;

    if (portfolioClone.riskProfile !== undefined) {
      delete portfolioClone.riskProfile._id;
    }

    const data = await dispatch(getPortfolioHoldingsData(portfolioClone));

    const validateIncomeStream = state.auth.user != null && userHasPermission(state.auth.user, PERMISSIONS.INCOME_STREAM);
    const options = {
      taxRate: selectCurrentTaxRate(state, portfolioClone.contact),
      shortHistory: comparison.shortHistory,
      comparison,
    };
    let item = portfolioToComparisonItem(portfolioClone, appRegion, validateIncomeStream, data, options);
    const forcedMonthlyRolling: boolean = shouldUseMonthlyRollingInReturns(comparison, state.auth.user);
    item = await recalcPortfolioItem(
      dispatch,
      state,
      item,
      forcedMonthlyRolling,
      comparison.shortHistory,
      comparison.showBaseCurrencies,
      true,
      comparison?.commonInception,
    );
    item.color = generateNewItemColor(items);

    if (comparison._id && !comparison._id.startsWith('new-')) {
      item.sharedFrom = comparison._id;
    }

    // If the source had a comparison id use it over the current one
    if (clonedItem && clonedItem.sharedFrom) {
      item.sharedFrom = clonedItem.sharedFrom;
    }

    // If the comparison item has any holdings override we want to pass that it is read only
    const isReadOnly = !!item.overrideHoldings?.length;

    dispatch(addItemAndRecalculateComparison(item));
    dispatch(loadPortfolioForEdit(portfolioClone, item, true, true, isReadOnly));
  };

export const loadNewPortfolio =
  (contact?: GenericContact, createItem = false): AsyncResult<void> =>
  async (dispatch, getState) => {
    const portfolio: Portfolio = getEmptyPortfolio();
    portfolio.contact = contact;
    let item;

    if (createItem) {
      const state = getState();
      const comparison = getCurrentStateComparison(state.comparison);

      if (!comparison) {
        return Promise.reject(new Error('No current comparison'));
      }

      const appRegion: AppRegion = state.auth.user != null ? state.auth.user.appConfig.appRegion : (AppRegion.CAN as AppRegion);
      const validateIncomeStream = state.auth.user != null && userHasPermission(state.auth.user, PERMISSIONS.INCOME_STREAM);

      const options = {
        comparison,
      };
      item = portfolioToComparisonItem(portfolio, appRegion, validateIncomeStream, undefined, options);
      const forcedMonthlyRolling: boolean = shouldUseMonthlyRollingInReturns(comparison, state.auth.user);
      item = await recalcPortfolioItem(
        dispatch,
        state,
        item,
        forcedMonthlyRolling,
        comparison.shortHistory,
        comparison.showBaseCurrencies,
        true,
        comparison?.commonInception,
      );
      item.color = generateNewItemColor(comparison.items);

      if (comparison._id && !comparison._id.startsWith('new-') && !item.sharedFrom) {
        item.sharedFrom = comparison._id;
      }

      dispatch(addNewPortfolioItemToComparison(item));
    }

    dispatch(logNewPortfolio());

    return dispatch(loadPortfolioForEdit(portfolio, item, false));
  };

export const setCalculationBenchmark =
  (benchmarkId: Nullable<string>): Result<void> =>
  async (dispatch, getState) => {
    dispatch({ ...SET_CALCULATION_BENCHMARK, benchmarkId });
    const state = getState();

    if (!benchmarkId) {
      const comparison = getCurrentStateComparison(state.comparison);

      if (!comparison) {
        throw new Error('No current comparison');
      }

      const { items } = comparison;
      const newItems = deepClone(items);

      for (const item of newItems) {
        if (!isBenchmark(item)) {
          if (item.type === COMPARISON_ITEM_TYPES.PORTFOLIO || item.type === COMPARISON_ITEM_TYPES.GROUP) {
            const forcedMonthlyRolling: boolean = shouldUseMonthlyRollingInReturns(comparison, state.auth.user);
            const newItem = await recalcPortfolioItem(
              dispatch,
              state,
              item,
              forcedMonthlyRolling,
              comparison.shortHistory,
              comparison.showBaseCurrencies,
              undefined,
              comparison.commonInception,
            );
            item.performance = overrideRiskMetrics(item, newItem.performance);
          } else if (item.originalRisk) {
            item.performance = overrideRiskMetrics(item, item.originalRisk);
          }
          const riskDate =
            item.performance.rolling && item.performance.rolling.monthly.length > 0
              ? new Date(item.performance.rolling.monthly[item.performance.rolling.monthly.length - 1][0])
              : null;
          item.dates = { ...item.dates, risk: { endDate: riskDate } };
        }
      }
      dispatch(setItemsAndCalculateComparisonCorrelations(newItems));
    }
  };

export const removeItem =
  (itemId: string): Result<void> =>
  (dispatch, getState) => {
    const comparisonState = getState().comparison;
    const comparison = getCurrentStateComparison(comparisonState);

    if (!comparison) {
      throw new Error('No current comparison');
    }

    const { items, selectedBenchmarkId } = comparison;
    const item = items.find(i => i._id === itemId);

    if (!item) {
      return;
    }

    // Clear any remote suitability results that may have been saved whenever a portfolio is removed
    if (item.type === COMPARISON_ITEM_TYPES.PORTFOLIO) {
      dispatch(clearSuitabilityAnalysis());
    }

    const groupsWithItem = comparison.items.filter(
      compItem => compItem.type === COMPARISON_ITEM_TYPES.GROUP && compItem.groupItems?.some(groupItem => item._id === groupItem.itemId),
    );

    groupsWithItem.forEach(group => {
      dispatch(removeItemFromGroup(item._id, group._id));
    });

    dispatch({ type: REMOVE_ITEM.type, itemId });
    dispatch(setComparisonCorrelations());
    dispatch(setComparisonShouldForceMonthlyData());
    dispatch(logRemoveFromComparison(item));

    if (isBenchmark(item) && selectedBenchmarkId && selectedBenchmarkId === itemId) {
      dispatch(setCalculationBenchmark(null));
    }

    // If the removed item was the only one currently filtered, reset filter to `all`
    const filteredItems = getComparisonFilteredItems(comparison, comparisonState.itemsFilter);
    if (filteredItems.length === 1 && filteredItems[0]._id === itemId) {
      dispatch(setComparisonItemsFilter([ComparisonItemsFilter.All]));
    }
    dispatch({ ...SET_SHOULD_UPDATE_COMMON_INCEPTION, shouldUpdateCommonInception: true });
  };

export const selectComparisonItem =
  (item: ComparisonItem, unselectOthers?: boolean, position?: number): Result<void> =>
  (dispatch, getState) => {
    const newSelectedItems = unselectOthers ? [] : getState().comparison.selectedComparisonItems.slice();

    if (!newSelectedItems.some(i => i._id === item._id)) {
      if (position !== undefined && position >= 0) {
        newSelectedItems.splice(position, 0, item);
      } else {
        newSelectedItems.push(item);
      }
    }

    dispatch({ ...SET_SELECTED_ITEMS, items: newSelectedItems });
  };

export const unselectComparisonItem =
  (item: ComparisonItem): Result<void> =>
  (dispatch, getState) => {
    const { selectedComparisonItems } = getState().comparison;
    const newSelectedItems = selectedComparisonItems.filter(i => i._id !== item._id);

    dispatch({ ...SET_SELECTED_ITEMS, items: newSelectedItems });
  };

export const combineIntoPortfolio = (): Result<void> => async (dispatch, getState) => {
  const state = getState();
  const comparison = getCurrentStateComparison(state.comparison);

  if (!comparison) {
    throw new Error('No current comparison');
  }

  const { selectedComparisonItems: items } = state.comparison;

  await dispatch(loadNewPortfolio(comparison.contact, true));

  dispatch(
    registerOnPortfolioCancel(() => {
      items.forEach(item => {
        dispatch(addItemAndRecalculateComparison(item));
      });
    }),
  );

  items.forEach((item: ComparisonItem) => {
    switch (item.type) {
      case COMPARISON_ITEM_TYPES.FUND: {
        if (item._id) {
          dispatch(addFundToPortfolioById(item._id));
        }
        break;
      }
      case COMPARISON_ITEM_TYPES.STOCK: {
        if (item._id) {
          dispatch(addStockToPortfolioById(item._id));
        }
        break;
      }
      case COMPARISON_ITEM_TYPES.ALT: {
        if (item._id) {
          dispatch(addAltToPortfolioById(item._id));
        }
        break;
      }
    }
    dispatch(removeItem(item._id));
  });
};

export const calculateIncomeStream =
  (startDate: Date, endDate: Date): Result<void> =>
  (dispatch, getState) => {
    const comparison = getCurrentStateComparison(getState().comparison);

    if (!comparison) {
      throw new Error('No current comparison');
    }

    const clonedItems = deepClone(comparison.items);

    const scenarioTemplate = {
      capital: 100000,
      amount: 0,
      unit: SCENARIO_UNIT.DOLLAR,
      type: SCENARIO_TYPE.WITHDRAWAL,
      frequency: SCENARIO_FREQUENCY.MONTHLY,
      distributionHarvest: 0,
    };

    for (const item of clonedItems) {
      if (!validIncomeStreamItem(item)) {
        if (item.scenarios != null) {
          delete item.scenarios;
        }
        continue;
      }

      const capital =
        item.scenarios && item.scenarios.defaultIncomeStream ? item.scenarios.defaultIncomeStream.capital : (item.source && item.source.size) || 100000;
      // Calculate Base Scenario
      const baseScenario = {
        ...scenarioTemplate,
        id: generateBSONId(),
        capital,
      };

      const baseScenarioData = calculateIncomeStreamData(item.holdings, baseScenario, startDate.getTime(), endDate.getTime());

      // Calculate Distribution Scenarios
      if (item.scenarios && item.scenarios.incomeStreamDistribution) {
        const distributionScenarios = deepClone(item.scenarios.incomeStreamDistribution);

        for (const scenario of distributionScenarios) {
          scenario.data = calculateIncomeStreamData(item.holdings, scenario, startDate.getTime(), endDate.getTime());
        }

        item.scenarios = {
          defaultIncomeStream: { ...baseScenario, data: baseScenarioData },
          incomeStreamDistribution: distributionScenarios,
        };
      } else {
        item.scenarios = {
          defaultIncomeStream: { ...baseScenario, data: baseScenarioData },
        };
      }
    }

    dispatch({ ...LOAD_DEFAULT_INCOME_STREAM, items: clonedItems });
  };

export const replacePortfolioInComparison =
  (portfolio: Portfolio, replaceId?: string, isIncomeTab?: boolean): AsyncResult<void> =>
  async (dispatch, getState) => {
    const state = getState();
    const comparison = getCurrentStateComparison(state.comparison);
    const isSelected = getState().comparison.selectedComparisonItems.some(i => i._id === replaceId);

    if (!comparison) {
      return Promise.reject(new Error('No current comparison'));
    }

    const items = comparison.items.slice();

    const indexToReplace = items.findIndex(i => i._id === (replaceId || portfolio._id));

    const data = await dispatch(getPortfolioHoldingsData(portfolio));

    const appRegion: AppRegion = state.auth.user != null ? state.auth.user.appConfig.appRegion : (AppRegion.CAN as AppRegion);
    const validateIncomeStream = state.auth.user != null && userHasPermission(state.auth.user, PERMISSIONS.INCOME_STREAM);
    const taxRate = selectCurrentTaxRate(state, portfolio.contact);
    const forcedMonthlyRolling: boolean = shouldUseMonthlyRollingInReturns(comparison, state.auth.user);
    const newPortfolio = reallocateAndRecalculatePortfolio(portfolio, data, forcedMonthlyRolling, taxRate, comparison.commonInception);

    const options = {
      taxRate,
      shortHistory: comparison.shortHistory,
      comparison,
    };
    let item = portfolioToComparisonItem(newPortfolio, appRegion, validateIncomeStream, data, options);
    item.color = items[indexToReplace].color;
    item.sharedFrom = items[indexToReplace].sharedFrom;
    item.isExpanded = items[indexToReplace].isExpanded;
    item.isFX = items[indexToReplace].isFX;
    item.scenarios = items[indexToReplace].scenarios;

    item = await recalcPortfolioItem(
      dispatch,
      state,
      item,
      forcedMonthlyRolling,
      comparison.shortHistory,
      comparison.showBaseCurrencies,
      undefined,
      comparison.commonInception,
    );

    items[indexToReplace] = item;

    dispatch(logAddToComparison(item));
    dispatch(setItemsAndCalculateComparisonCorrelations(items));

    if (isIncomeTab) {
      const incomeStartDate = getIncomeStartDate(comparison);
      const incomeEndDate = getIncomeEndDate(comparison);
      dispatch(calculateIncomeStream(incomeStartDate, incomeEndDate));
    }

    if (isSelected) {
      dispatch(selectComparisonItem(item));
    }
  };

export const calculateHistoricalScenarios =
  (historicalScenarios: HistoricalScenario[], togetherMode: boolean): AsyncResult<CalculatedHistoricalScenario[]> =>
  async (_dispatch, getState) => {
    const { comparison } = getState();
    const currentComparison = getCurrentStateComparison(comparison);

    if (!currentComparison) {
      return Promise.reject(new Error('No current comparison'));
    }

    if (currentComparison?.isDirty) {
      const items = currentComparison.items.map(item => {
        const newItem = deepClone(item);
        return {
          _id: newItem._id,
          holdings: newItem.holdings.map(h => ({ id: h.id, type: h.type, weight: h.weight, yieldParams: h.yieldParams })),
        };
      });
      return api.post(CALCULATE_HISTORICAL_SCENARIOS, null, { items, historicalScenarios, togetherMode });
    }
    const params: { id: string; contactId?: string } = { id: currentComparison._id! };
    if (currentComparison.contact != null) {
      params.contactId = currentComparison.contact._id;
    }
    return api.post(CALCULATE_COMPARISON_HISTORICAL_SCENARIOS, params, { historicalScenarios, togetherMode });
  };

export const updateComparison =
  (id: string, body: Partial<Comparison>, isNewlyAssigned?: boolean): AsyncResult<Comparison> =>
  async () => {
    const params: { id: string; isNewlyAssigned?: boolean } = { id };
    if (isNewlyAssigned) {
      params.isNewlyAssigned = true;
    }
    return api.put(UPDATE_COMPARISON, params, body);
  };

export const saveComparison =
  (isNewlyAssigned?: boolean): AsyncResult<Comparison> =>
  async (dispatch, getState) => {
    const {
      comparison: updatedComparison,
      comparison: { selectedComparisonItems },
    } = getState();
    const comparison = getCurrentStateComparison(updatedComparison);

    if (!comparison) {
      return Promise.reject(new Error('No current comparison'));
    }

    dispatch({ ...SET_IS_SAVING, isSaving: true });

    const data = deepClone(comparison);

    for (let item of data.items) {
      delete item.source;

      // we should never save a correlation matrix -- we will always compute it from the rollings
      delete item.performance.correlation;
      if (item.performanceBase) {
        delete item.performanceBase.correlation;
      }

      // If we're saving a draft, we shouldn't be persisting the returns -- we'll always use the most recent data
      if (!data.contact && !data.householdId) {
        for (const holding of item.holdings) {
          delete holding.rolling;
          delete holding.nav;
          delete holding.distribution;
          delete holding.baseRolling;
        }
      }

      // Remove any comments that weren't posted so that they aren't saved
      item = Object.assign(item, deleteUnpostedComments(item));
    }

    if (selectedComparisonItems.length) {
      dispatch(unselectAllComparisonItems());
    }

    // handle autoMonthEndData
    if (data.autoMonthEndData) {
      data.monthEndDataOption = PERIOD_END_DATA_OPTIONS.AUTO;
    }

    // handle when comparison has an omniRiskProfile
    if (data.riskProfile && 'isOmniRiskProfile' in data.riskProfile) {
      const { isOmniRiskProfile, ...rest } = data.riskProfile;
      Object.assign(data, { omniRiskProfile: rest });
      delete data.riskProfile;
    }

    if (data.accountSets && data.accountSets.length > 0) {
      data.accountSets = sanitizeAccountSetsForSave(data.accountSets);
    }

    let savePromise;
    if (!data._id || data._id.startsWith('new-')) {
      delete data._id;
      savePromise = api.post(SAVE_COMPARISON, null, { ...data });
    } else {
      const items = comparison.items.slice();

      for (let item of items) {
        // Remove any comments that weren't posted so that they aren't saved
        item = Object.assign(item, deleteUnpostedComments(item));
      }

      data.items = mapComparisonId(items, data._id) as ComparisonItem[];

      const params: { id: string; isNewlyAssigned?: boolean } = {
        id: data._id,
      };

      if (isNewlyAssigned) {
        params.isNewlyAssigned = true;
      }

      savePromise = api.put(UPDATE_COMPARISON, params, { ...data });
    }

    return savePromise.then((res: Comparison) => {
      dispatch({ type: POST_SAVE_COMPARISON.type, id: res._id });
      dispatch({ ...SET_IS_SAVING, isSaving: false });
      return { ...comparison, _id: res._id };
    });
  };

export const closeComparison =
  (comparison: Comparison, resetContact: boolean = true): Result<void> =>
  dispatch => {
    if (comparison.gifItems) {
      dispatch(resetGif());
    }

    dispatch({ type: CLOSE_COMPARISON.type, comparison });

    if (resetContact) {
      dispatch(resetCurrentContact());
    }

    dispatch(clearCurrentHousehold());

    dispatch(resetQuestionnaire(QUESTIONNAIRE_TYPES.RATIONALE));
    // Clear any remote suitability results that may have been saved whenever comparison is closed
    dispatch(clearSuitabilityAnalysis());
  };

export const deleteComparison =
  (comparison: ReducedComparison | Comparison): AsyncResult<void> =>
  () => {
    const params: { id?: string; comparisonType: string; contactId?: string } = {
      id: comparison._id,
      comparisonType: comparison.comparisonType,
    };
    if (comparison.contact != null) {
      params.contactId = comparison.contact._id;
    }
    return api.delete(DELETE_COMPARISON, params);
  };

export const sharedWithClientComparison =
  (comparison: ReducedComparison | Comparison): AsyncResult<void> =>
  dispatch => {
    if (!comparison._id) return Promise.resolve();

    const params = { id: comparison._id };

    return api
      .patch(SHARED_WITH_CLIENT, params)
      .then(() => dispatch(getComparisonById(comparison._id!, comparison.contact)))
      .then(updatedComparison => {
        if (updatedComparison && hasComparisonFlag(updatedComparison, ComparisonFlagsType.SHARED_WITH_CLIENT)) {
          dispatch(updateEmbeddedContactComparison(updatedComparison));
        }
      });
  };

export const createComparison =
  (contact?: GenericContact, household?: GenericHouseHold): AsyncResult<Comparison> =>
  (dispatch, getState) => {
    const {
      auth: { user },
    } = getState();

    const comparison = generateDraftComparison(user);

    if (contact) {
      comparison.name = formatMessage('Comparison.NewForClient', { name: contact.fname || contact.email || '' });
      comparison.contact = createComparisonContact(contact);
      comparison.comparisonType = COMPARISON_TYPE.CONTACT;
      if (userHasPermission(user, PERMISSIONS.CONTACTS_LOCALE) && contact.locale != null) {
        comparison.proposalLocale = contact.locale;
      }
    }

    if (household) {
      comparison.name = formatMessage('Comparison.NewForHousehold', { name: household.name || '' });
      comparison.householdId = household.id;
      comparison.contacts = household.contacts.map(createComparisonContact);
      comparison.householdName = household.name;
      comparison.comparisonType = COMPARISON_TYPE.CONTACT;
    }

    dispatch(logNewComparison());

    return dispatch(loadComparison(comparison));
  };

export const skipEmptyState = (): Result<void> => dispatch => {
  dispatch({ ...SKIP_EMPTY_STATE });
};

export const replaceGifItemInComparison =
  (item: BMOGIFItem): AsyncResult<void> =>
  async (dispatch, getState) => {
    const { comparison, bmoGif } = getState();
    const currentComparison = getCurrentStateComparison(comparison);
    const { portfolio } = bmoGif;

    if (!currentComparison) {
      return;
    }
    const inValidAge = currentComparison.contact && isInvalidDepositAge(portfolio, currentComparison.contact);
    if (inValidAge && !inValidAge.isInvalid) {
      const gifItems = [item];
      dispatch({ ...SET_BMO_GIF_ITEMS, gifItems });
    }
  };

export const removeGifItem = (): AsyncResult<void> => async (dispatch, getState) => {
  const currentComparison = getCurrentStateComparison(getState().comparison);

  if (!currentComparison) {
    return;
  }

  dispatch({ ...SET_BMO_GIF_ITEMS, gifItems: undefined });
};

export const rerunComparison =
  (comparison: Comparison): AsyncResult<Comparison> =>
  (dispatch, getState) => {
    const state = getState();

    dispatch(startLoaderAction(SET_IS_RERUNNING.type));

    return new Promise(async (resolve, reject) => {
      let data = deepClone(comparison) as Comparison;

      // handle autoMonthEndData
      if (data.monthEndDataOption === PERIOD_END_DATA_OPTIONS.AUTO && state.auth.user?.preferences?.autoPerformanceAndRiskData) {
        data.monthEndDataOption = state.auth.user?.preferences?.autoPerformanceAndRiskData as PERIOD_END_DATA_OPTIONS;
        data.autoMonthEndData = true;
      }

      // handle removing shared with client flag
      if (hasComparisonFlag(data, ComparisonFlagsType.SHARED_WITH_CLIENT)) {
        data.flags = data.flags.filter(flag => flag !== ComparisonFlagsType.SHARED_WITH_CLIENT);
      }

      if (data.items.some(item => !item._id)) {
        dispatch(stopLoaderAction(SET_IS_RERUNNING.type));
        return reject();
      }

      data.name = generateItemCopyName(data.name, 'Comparison.CopyPrefix');
      data._id = generateTempId();
      data.correlationId = data._id?.replace('new-', '');
      delete data.lastRunAt;

      // Update startDate and endDate for all charts if present
      if (data.options) {
        data.options = updateChartDatesToToday(data.options, data.monthEndDataOption);
      }

      data.commonInception = getCommonInception(data, state.comparison.itemsFilter, comparison.shortHistory);
      data = await refreshComparisonData(dispatch, state, data);

      if (comparisonHasMissingOrModifiedItems(data)) {
        dispatch(stopLoaderAction(SET_IS_RERUNNING.type));
        return resolve(data);
      }

      // Clear any remote suitability results that may have been saved whenever comparison is closed
      dispatch(clearSuitabilityAnalysis());

      // Flag as dirty to allow directly saving the refreshed comparison
      data.isDirty = true;

      dispatch(stopLoaderAction(SET_IS_RERUNNING.type));

      return resolve(data);
    });
  };

export const setComparisonName =
  (name: string): Result<void> =>
  dispatch => {
    dispatch({ type: SET_COMPARISON_NAME.type, name });
  };

export const setComparisonContact =
  (contact: Nullable<GenericContact>): Result<void> =>
  (dispatch, getState) => {
    const {
      auth: { user },
      comparison,
    } = getState();

    if (!contact) {
      return;
    }

    const currentComparison = getCurrentStateComparison(comparison);

    if (currentComparison) {
      const comparisonContact = createComparisonContact(contact);

      dispatch({ type: SET_COMPARISON_CONTACT.type, comparisonContact });

      // if proposalLocale was already set, we keep it
      if (currentComparison.proposalLocale == null && userHasPermission(user, PERMISSIONS.CONTACTS_LOCALE) && contact.locale != null) {
        dispatch(setProposalLocale(contact.locale));
      }
    }
  };

export const getComparisonById =
  (id: string, contact?: ComparisonContact): AsyncResult<Comparison> =>
  () => {
    const params: { id: string; contactId?: string } = { id };
    if (contact != null && contact._id != null) {
      params.contactId = contact._id;
    }
    return api.get(GET_COMPARISON_BY_ID, params);
  };

export const getRecentComparisons =
  (query?: string, nextToken?: string): AsyncResult<ReducedComparison[]> =>
  () => {
    const params: {
      limit: number;
      comparisonType: string;
      excludeKeys: string;
      next?: string;
      searchQuery?: string;
      searchableFields?: string;
    } = {
      limit: MAX_LOADED_COMPARISONS,
      comparisonType: [COMPARISON_TYPE.DRAFT, COMPARISON_TYPE.CONTACT].join(','),
      excludeKeys: 'items,gifItems',
    };

    if (nextToken) {
      params.next = nextToken;
    }

    if (query) {
      params.searchQuery = query;
      params.searchableFields = 'name,contact.fullName';
    }

    return api.get(GET_RECENT_COMPARISONS, params).then((res: { data: ReducedComparison[] }) => res.data);
  };

export const getAssignedComparisons =
  (query?: string, nextToken?: string): AsyncResult<ReducedComparison[]> =>
  () => {
    const params: {
      limit: number;
      comparisonType: string;
      excludeKeys: string;
      next?: string;
      searchQuery?: string;
      searchableFields?: string;
    } = {
      limit: MAX_LOADED_COMPARISONS,
      comparisonType: COMPARISON_TYPE.CONTACT,
      excludeKeys: 'items,gifItems',
    };

    if (nextToken) {
      params.next = nextToken;
    }

    if (query) {
      params.searchQuery = query;
      params.searchableFields = 'name,contact.fullName';
    }

    return api.get(GET_ASSIGNED_COMPARISONS, params).then((res: { data: ReducedComparison[] }) => res.data);
  };

export const getDraftComparisons =
  (query?: string, nextToken?: string): AsyncResult<ReducedComparison[]> =>
  () => {
    const params: {
      limit: number;
      comparisonType: string;
      shared: boolean;
      excludeKeys: string;
      next?: string;
      searchQuery?: string;
      searchableFields?: string;
    } = {
      limit: MAX_LOADED_COMPARISONS,
      comparisonType: COMPARISON_TYPE.DRAFT,
      shared: false,
      excludeKeys: 'items,gifItems',
    };

    if (nextToken) {
      params.next = nextToken;
    }

    if (query) {
      params.searchQuery = query;
      params.searchableFields = 'name,contact.fullName';
    }

    return api.get(GET_DRAFT_COMPARISONS, params).then((res: { data: ReducedComparison[] }) => res.data);
  };

export const getComplianceComparisons =
  (query?: string, nextToken?: string): AsyncResult<ReducedComparison[]> =>
  () => {
    const params: {
      limit: number;
      comparisonType: string;
      shared: boolean;
      excludeKeys: string;
      next?: string;
      searchQuery?: string;
      searchableFields?: string;
    } = {
      limit: MAX_LOADED_COMPARISONS,
      comparisonType: COMPARISON_TYPE.COMPLIANCE,
      shared: false,
      excludeKeys: 'items,gifItems',
    };

    if (nextToken) {
      params.next = nextToken;
    }

    if (query) {
      params.searchQuery = query;
      params.searchableFields = 'name,contact.fullName';
    }

    return api.get(GET_COMPLIANCE_COMPARISONS, params).then((res: { data: ReducedComparison[] }) => res.data);
  };

export const setComment =
  (comment: string): Result<void> =>
  dispatch => {
    dispatch({ type: SET_COMMENT.type, comment });
  };

export const shareComparison =
  (infos: SharedComparisonInfos): AsyncResult<Nullable<string>> =>
  (dispatch, getState) => {
    const {
      auth: { user },
      comparison,
    } = getState();
    const currentComparison = getCurrentStateComparison(comparison);

    if (!currentComparison) {
      return Promise.reject(new Error('No current comparison'));
    }

    const comparisonInfos = deepClone(infos);

    if (currentComparison._id && currentComparison._id.indexOf('new-') === -1) {
      comparisonInfos._id = currentComparison._id;
    }

    let sharingInfo: Nullable<SharingInfo> = null;
    if (user) {
      sharingInfo = getSharingInfo(user);
    }

    const sharedComparison = generateSharedComparison(currentComparison, sharingInfo);
    comparisonInfos.comparison = sharedComparison;

    return api.post(SHARE_COMPARISON, null, comparisonInfos).then((res: { data: string }) => res.data);
  };

export const validateComparisonToken =
  (token: string): AsyncResult<string> =>
  () =>
    api.get(VALIDATE_COMPARISON_TOKEN, { token }).then((res: { data: string }) => res.data);

export const replaceBenchmarkInComparison =
  (benchmarkPortfolio: BenchmarkPortfolio, tempId?: string): Result<void> =>
  async (dispatch, getState) => {
    const comparison = getCurrentStateComparison(getState().comparison);

    if (!comparison) {
      throw new Error('No current comparison');
    }

    const items = comparison.items.slice();
    const indexToReplace = items.findIndex(i => i._id === (tempId || benchmarkPortfolio._id));

    const data = await dispatch(getBenchmarksData(benchmarkPortfolio));

    const forcedMonthlyRolling: boolean = comparison != null ? shouldUseMonthlyRollingInReturns(comparison, getState().auth.user) : false;
    const newBenchmarkPortfolio = await calculateBenchmarkPortfolioStats(
      dispatch,
      benchmarkPortfolio,
      data,
      forcedMonthlyRolling,
      comparison.shortHistory,
      comparison.commonInception,
    );

    let item = benchmarkToComparisonItem(newBenchmarkPortfolio, data, comparison.commonInception);
    item.color = items[indexToReplace].color;
    item = await recalcBenchmarkItem(dispatch, item, forcedMonthlyRolling, comparison, comparison.shortHistory);

    items[indexToReplace] = item;
    dispatch(setItemsAndCalculateComparisonCorrelations(items));
  };

export const cloneBenchmark =
  (benchmarkPortfolio: BenchmarkPortfolio): AsyncResult<void> =>
  async (dispatch, getState) => {
    const comparison = getCurrentStateComparison(getState().comparison);

    if (!comparison) {
      throw new Error('No current comparison');
    }

    const items = comparison.items.slice();

    const benchmarkClone = deepClone(benchmarkPortfolio);
    const clonedItem = items.find(item => item._id === benchmarkClone._id);

    benchmarkClone._id = generateTempId();
    benchmarkClone.name = generateItemCopyName(benchmarkClone.name, 'Benchmarks.CopyPrefix');

    if (benchmarkClone.dates.savedAt) {
      delete benchmarkClone.dates.savedAt;
    }

    const data = await dispatch(getBenchmarksData(benchmarkClone));
    const forcedMonthlyRolling: boolean = comparison != null ? shouldUseMonthlyRollingInReturns(comparison, getState().auth.user) : false;
    const newBenchmarkPortfolio = await calculateBenchmarkPortfolioStats(
      dispatch,
      benchmarkClone,
      data,
      forcedMonthlyRolling,
      comparison.shortHistory,
      comparison?.commonInception,
    );
    const item = benchmarkToComparisonItem(newBenchmarkPortfolio, data, comparison?.commonInception);
    item.color = generateNewItemColor(items);

    if (comparison._id && !comparison._id.startsWith('new-')) {
      item.sharedFrom = comparison._id;
    }

    if (clonedItem && clonedItem.sharedFrom) {
      // If the source had a comparison id, use it over the current one
      item.sharedFrom = clonedItem.sharedFrom;
    }

    dispatch(addItemAndRecalculateComparison(item));
    dispatch(loadBenchmarkForEdit(newBenchmarkPortfolio));
  };

export const attachOmniRiskProfile =
  (omniFormattedRiskProfile: FormattedFullOmniRiskProfile): AsyncResult<void> =>
  async (dispatch, getState) => {
    dispatch(startLoaderAction(SET_IS_ATTACHING_OMNI_RISK_PROFILE.type));
    const state = getState();
    const {
      auth: { user },
      contacts: { current },
    } = state;

    const contact = current != null ? current.contact : undefined;
    let comparison = getCurrentStateComparison(state.comparison);

    if (!comparison || comparisonIsReadOnly(comparison)) {
      comparison = generateDraftComparison(user, contact);
      await dispatch(loadComparison(comparison));
    }

    const { formData, formId, profile } = omniFormattedRiskProfile;
    const omniRiskProfile = {
      ...profile!, // when a risk profile is APPROVED, the profile exists
      _id: profile!.id,
      formId,
      savedSections: formData?.savedSections,
    };

    if (profile?.alternativeModels && profile.alternativeModels.length > 0) {
      await addAlternativeModelsToComparison(dispatch, state, comparison, profile.alternativeModels);
    }

    logAttachOmniRiskProfileToComparison(comparison, {
      ...omniRiskProfile,
      savedSections: omniRiskProfile.savedSections,
    } as Partial<OmniRiskProfileConfig>);
    dispatch({ ...ATTACH_OMNI_RISK_PROFILE, profile: omniRiskProfile });
    dispatch(stopLoaderAction(SET_IS_ATTACHING_OMNI_RISK_PROFILE.type));
  };

export const attachRiskProfile =
  (profile: RiskProfileItem): AsyncResult<void> =>
  async (dispatch, getState) => {
    dispatch(startLoaderAction(SET_IS_ATTACHING_RISK_PROFILE.type));
    const state = getState();
    const contact = state.contacts.current != null ? state.contacts.current.contact : undefined;
    let comparison = getCurrentStateComparison(state.comparison);
    const {
      auth: { user },
    } = state;

    if (!comparison || comparisonIsReadOnly(comparison)) {
      comparison = generateDraftComparison(user, contact);
      await dispatch(loadComparison(comparison));
    }

    if (profile.alternativeModels && profile.alternativeModels.length > 0) {
      await addAlternativeModelsToComparison(dispatch, state, comparison, profile.alternativeModels);
    }

    dispatch({ ...ATTACH_RISK_PROFILE, profile });
    dispatch(logAttachRiskProfile(profile));
    dispatch(stopLoaderAction(SET_IS_ATTACHING_RISK_PROFILE.type));
  };

export const addRationaleAnswers =
  (answersId: string): Result<void> =>
  async (dispatch, getState) => {
    dispatch({ ...ADD_RATIONALE_ANSWERS, answersId });
  };

export const attachPortfolioRiskProfile =
  (profile: RiskProfileItem): Result<void> =>
  dispatch => {
    dispatch(startLoaderAction(SET_IS_ATTACHING_PORTFOLIO_RISK_PROFILE.type));
    dispatch({ ...ATTACH_PORTFOLIO_RISK_PROFILE, profile });
    dispatch(stopLoaderAction(SET_IS_ATTACHING_PORTFOLIO_RISK_PROFILE.type));
  };

export const setSelectedCategories =
  (selectedCategories?: SelectedCategories): Result<void> =>
  dispatch => {
    dispatch({ ...SET_SELECTED_CATEGORIES, selectedCategories });
  };

export const applyMarkupToCell =
  (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField, markupTool: string): Result<void> =>
  dispatch => {
    const currentItem = deepClone(item);
    const markup = { ...item.markup };

    switch (markupTool) {
      case MARKUP_TOOLS.HIGHLIGHT: {
        currentItem.markup = highlightField(category.key, field.key, markup);
        break;
      }
      case MARKUP_TOOLS.SQUARE: {
        currentItem.markup = outlineField(category.key, field.key, markup, MARKUP_TOOLS.SQUARE);
        break;
      }
      case MARKUP_TOOLS.CIRCLE: {
        currentItem.markup = outlineField(category.key, field.key, markup, MARKUP_TOOLS.CIRCLE);
        break;
      }
    }

    dispatch({ ...APPLY_MARKUP, item: currentItem });
    dispatch(logAddComparisonMarkup(category.key, field.key, markupTool));
  };

export const setMarkupComment =
  (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField, value: string): Result<void> =>
  dispatch => {
    const currentItem = deepClone(item);
    const markup = { ...item.markup };

    const comment = markup[category.key] && markup[category.key][field.key] && markup[category.key][field.key].comment;

    if (comment && comment.isPosted) {
      currentItem.markup = updateMarkupComment(category.key, field.key, markup, value, comment.dotNumber, comment.timestamp, comment.isPosted);
    } else {
      currentItem.markup = updateMarkupComment(category.key, field.key, markup, value);
    }

    dispatch({ type: SET_MARKUP_COMMENT.type, item: currentItem });
  };

export const postMarkupComment =
  (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField, dotNumber: number, value: string): Result<void> =>
  dispatch => {
    const currentItem = deepClone(item);
    const markup = { ...item.markup };

    currentItem.markup = updateMarkupComment(category.key, field.key, markup, value, dotNumber, new Date(), true);

    dispatch({ type: POST_MARKUP_COMMENT.type, item: currentItem });
    dispatch(logAddComparisonMarkup(category.key, field.key, MARKUP_TOOLS.COMMENT));
  };

export const clearAllMarkup = (): Result<void> => (dispatch, getState) => {
  const comparison = getCurrentStateComparison(getState().comparison);

  if (!comparison) {
    throw new Error('No current comparison');
  }

  const clonedItems = deepClone(comparison.items) as ComparisonItem[];

  for (const item of clonedItems) {
    if (item.markup) {
      item.markup = undefined;
      dispatch({ ...CLEAR_ALL_MARKUP, item });
    }
  }

  dispatch(logResetComparisonMarkup());
};

export const clearMarkup =
  (item: ComparisonItem, categoryKey: string, fieldKey: string, markupTool: string): Result<void> =>
  dispatch => {
    const currentItem = deepClone(item);

    if (!currentItem.markup) {
      return;
    }

    switch (markupTool) {
      case MARKUP_TOOLS.HIGHLIGHT: {
        delete currentItem.markup[categoryKey][fieldKey].highlight;
        break;
      }
      case MARKUP_TOOLS.SQUARE:
      case MARKUP_TOOLS.CIRCLE: {
        delete currentItem.markup[categoryKey][fieldKey].shape;
        break;
      }
      case MARKUP_TOOLS.COMMENT: {
        delete currentItem.markup[categoryKey][fieldKey].comment;
        break;
      }
    }

    dispatch({ ...CLEAR_MARKUP, item: currentItem });
  };

export const updateSelectedCategories =
  (previousCategories: ComparisonCategory[], currentCategories: ComparisonCategory[], duplicateFields?: string[]): Result<void> =>
  (dispatch, getState) => {
    const comparison = getCurrentStateComparison(getState().comparison);

    if (!comparison) {
      throw new Error('No current comparison');
    }
    const { selectedCategories } = comparison;

    // If selected categories isnt initialized yet we dont update
    if (!selectedCategories) {
      return;
    }

    const oldSelectedCategories = deepClone(selectedCategories) as SelectedCategories;

    // Added or removed categories should toggle the entire category
    const addedCategories = diffArray(currentCategories, previousCategories, 'key');
    const removedCategories = diffArray(previousCategories, currentCategories, 'key');
    updateDiffCategories(dispatch, addedCategories, removedCategories, duplicateFields);

    // Unchanged categories might have fields that are added/removed so we have to toggle them manually
    const unchangedCategories = currentCategories.filter(cat => !previousCategories.includes(cat));
    updateUnchangedCategories(dispatch, unchangedCategories, previousCategories, currentCategories, oldSelectedCategories);

    if (duplicateFields) {
      const updatedDuplicateFields = getDuplicateFields(currentCategories);
      const keyMetrics = currentCategories.find(c => c.key === COMPARISON_CATEGORIES.KEY_METRICS);
      const addedDuplicates = diffArray(updatedDuplicateFields, duplicateFields);
      const removedDuplicates = diffArray(duplicateFields, updatedDuplicateFields);
      const updatedComparison = getCurrentStateComparison(getState().comparison);

      if (!updatedComparison) {
        return;
      }

      const { selectedCategories: newSelectedCategories } = updatedComparison;

      if (newSelectedCategories && keyMetrics) {
        // Added duplicates (ex. minInvestments in fund => fund + portfolio comparison)
        // Since we link the duplicate fields we need to toggle the source field only if it was previously toggled beforehand
        for (const fieldKey of addedDuplicates) {
          const field = keyMetrics.fields.find(f => f.key === fieldKey);

          if (field) {
            // Check if the field was previously selected
            const isSelected = fieldIsSelected(field, keyMetrics, oldSelectedCategories);

            // Find the category where the new duplicate source is
            const category = currentCategories.find(c => c.key !== keyMetrics.key && c.fields.some(f => f.key === field.key));

            // Whenever a duplicate is added, we will always filter it from keyMetrics since it is no longer the source
            let newFields = newSelectedCategories[keyMetrics.key].fields.slice();
            newFields = newFields.filter((f: string) => f !== field.key);

            dispatch({ ...TOGGLE_FIELD, key: keyMetrics.key, fields: newFields });

            // If the field was not previously selected, we have to toggle it off (since it would be toggled on in the updateDiffCategories function)
            if (!isSelected && category) {
              dispatch(toggleField(category, field));
            }
          }
        }

        // Removed duplicate (ex. minInvestments in fund + portfolio => fund comparison)
        for (const fieldKey of removedDuplicates) {
          const field = keyMetrics.fields.find(f => f.key === fieldKey);

          if (field) {
            // Find if duplicate field was selected in a different category prior
            const isSelected = fieldIsSelected(field, keyMetrics, oldSelectedCategories);

            // If it was previously selected then toggle it on in the keyMetrics section
            if (isSelected) {
              dispatch(toggleField(keyMetrics, field));
            }
          }
        }
      }
    }
  };

export const removeRiskProfile = (): Result<void> => dispatch => {
  dispatch(CLEAR_RISK_PROFILE);
};

export const removePortfolioRiskProfile = (): Result<void> => dispatch => {
  dispatch(CLEAR_PORTFOLIO_RISK_PROFILE);
};

export const setRiskPeriod =
  (months: Nullable<number>): Result<void> =>
  dispatch => {
    dispatch({ ...SET_RISK_PERIOD, period: monthsToPeriod(months) });
  };

export const setFundRiskPeriod =
  (months: Nullable<number>): Result<void> =>
  dispatch => {
    dispatch({ ...SET_FUND_RISK_PERIOD, period: monthsToPeriod(months) });
  };

export const setPortfolioRiskPeriod =
  (months: Nullable<number>): Result<void> =>
  dispatch => {
    dispatch({ ...SET_PORTFOLIO_RISK_PERIOD, period: monthsToPeriod(months) });
  };

export const setGroupRiskPeriod =
  (months: Nullable<number>): Result<void> =>
  dispatch => {
    dispatch({ ...SET_GROUP_RISK_PERIOD, period: monthsToPeriod(months) });
  };

export const setCalendarYearPeriod =
  (months: Nullable<number>): Result<void> =>
  dispatch => {
    dispatch({ ...SET_CALENDAR_YEAR_PERIOD, period: monthsToPeriod(months) });
  };

export const setFundCalendarYearPeriod =
  (months: Nullable<number>): Result<void> =>
  dispatch => {
    dispatch({ ...SET_FUND_CALENDAR_YEAR_PERIOD, period: monthsToPeriod(months) });
  };

export const setPortfolioCalendarYearPeriod =
  (months: Nullable<number>): Result<void> =>
  dispatch => {
    dispatch({ ...SET_PORTFOLIO_CALENDAR_YEAR_PERIOD, period: monthsToPeriod(months) });
  };

export const setGroupCalendarYearPeriod =
  (months: Nullable<number>): Result<void> =>
  dispatch => {
    dispatch({ ...SET_GROUP_CALENDAR_YEAR_PERIOD, period: monthsToPeriod(months) });
  };

export const setExpandPortfolioItem =
  (item: ComparisonItem, isExpanded: boolean): Result<void> =>
  (dispatch, getState) => {
    const {
      editPortfolio: { comparisonItem: editedComparisonItem },
    } = getState();

    const comparison = getCurrentStateComparison(getState().comparison);

    if (!comparison) {
      throw new Error('No current comparison');
    }

    // Find the items of the current comparison
    const { items } = comparison;

    // Clone the items for immutability
    const newItems = deepClone(items);

    // Set the desired item as expanded (or collapsed)
    const expandedItem = newItems.find(i => i._id === item._id);

    if (!expandedItem || item.isExpanded === isExpanded) {
      return;
    }

    expandedItem.isExpanded = isExpanded;
    expandedItem.isFX = false; // Currency conversion cannot be used at the same time as the portfolio expand (on the same item).

    dispatch({ ...SET_ITEMS, items: newItems });

    // If portfolio was also being edited, also expand the copy made for editing
    if (editedComparisonItem && item._id === editedComparisonItem._id) {
      dispatch(setExpandComparisonItem(isExpanded));
    }
  };

export const getComparisonItemBenchmarks =
  (items: ComparisonItem[]): AsyncResult<Dictionary<Benchmark>> =>
  async dispatch => {
    const allHoldings: ComparisonHolding[] = [];

    for (const item of items) {
      if (item.holdings) {
        allHoldings.push(...item.holdings);
      }
    }

    if (allHoldings.length === 0) {
      return {};
    }

    try {
      const holdingsData = await dispatch(getItemHoldingsData(allHoldings));
      const { benchmarks } = holdingsData;

      const lipperIdToBenchmarkMap: Dictionary<Benchmark> = benchmarks.reduce((map, benchmark) => {
        map[benchmark.lipperId] = benchmark;
        return map;
      }, {} as Dictionary<Benchmark>);

      return lipperIdToBenchmarkMap;
    } catch (error) {
      console.error('Error fetching comparison benchmarks:', error);
      return {};
    }
  };

export const getItemHoldingsData =
  (holdings: ComparisonHolding[]): AsyncResult<HoldingsData> =>
  async (dispatch, getState) => {
    const fundsHoldings = holdings.filter(h => h.type === HOLDING_TYPES.FUND);
    const stocksHoldings = holdings.filter(h => h.type === HOLDING_TYPES.STOCK);
    const altsHoldings = holdings.filter(h => h.type === HOLDING_TYPES.ALT);
    const customYieldHoldings = holdings.filter(h => h.type === HOLDING_TYPES.CUSTOM_YIELD);

    const monthEndDataOption = getMonthEndDataOption(getState().comparison);

    const [funds, stocks, alts, customYields] = await Promise.all([
      dispatch(getManyFundsData(fundsHoldings.map(f => f.id))),
      dispatch(getManyStocksData(stocksHoldings.map(s => s.id))),
      dispatch(getManyAltsData(altsHoldings.map(a => a.id))),
      Promise.all(
        customYieldHoldings.map(holding =>
          getCachedCustomYieldFund(
            holding.yieldParams!,
            {
              filteredShelves: holding.filteredShelves as Shelf[],
              complianceComparisons: holding.complianceComparisons,
            },
            holding.currency,
            holding.id,
            monthEndDataOption,
          ),
        ),
      ),
    ]);

    const calculatedYields = await Promise.all(customYields.map(f => dispatch(calculateCustomFund(f))));

    const [fundBenchmarks, stockBenchmarks, customYieldBenchmarks] = await Promise.all([
      dispatch(getFundsBenchmarks(funds)),
      dispatch(getStocksBenchmarks(stocks)),
      dispatch(getFundsBenchmarks(customYields)),
    ]);

    const formattedBenchmarks = stockBenchmarks.map(comparisonBenchmarkToBenchmark);
    return {
      funds: funds.concat(calculatedYields),
      stocks,
      alts,
      benchmarks: [...fundBenchmarks, ...formattedBenchmarks, ...customYieldBenchmarks],
    };
  };

export const calculateCustomBenchmarks =
  (item: ComparisonItem): Result<void> =>
  (dispatch, getState) => {
    if (benchmarkIsFull(item.holdings)) {
      const comparison = getCurrentStateComparison(getState().comparison);
      if (!comparison) {
        throw new Error('No current comparison');
      }
      const { items } = comparison;
      dispatch({ ...SET_ITEMS, items });
      dispatch({ ...SET_SHOULD_UPDATE_COMMON_INCEPTION, shouldUpdateCommonInception: true });
    }
  };

export const clearComparisonsCache = (): Result<void> => dispatch => {
  dispatch({ ...CLEAR_COMPARISONS_CACHE });
};

export const showBaseCurrencies =
  (useBaseCurrency: boolean): AsyncResult<void> =>
  async (dispatch, getState) => {
    const state = getState();
    const { comparison: comparisonState } = state;
    const comparison = getCurrentStateComparison(comparisonState);

    if (!comparison) {
      throw new Error('No current comparison');
    }

    // Clone the items for immutability
    const newItems = deepClone(comparison.items);
    const promises = [] as Promise<ComparisonItem>[];

    if (useBaseCurrency) {
      // Calculate base performance if it's not already present on the item

      for (let i = 0; i < newItems.length; i++) {
        const item = newItems[i];

        switch (item.type) {
          case COMPARISON_ITEM_TYPES.PORTFOLIO: {
            const forcedMonthlyRolling: boolean = shouldUseMonthlyRollingInReturns(comparison, state.auth.user);
            promises.push(
              recalcPortfolioItem(dispatch, state, item, forcedMonthlyRolling, comparison.shortHistory, useBaseCurrency, undefined, comparison.commonInception),
            );
            break;
          }
          default:
            promises.push(Promise.resolve(item));
            break;
        }

        newItems[i] = item;
      }

      Promise.all(promises).then(items => {
        dispatch({ ...SET_ITEMS, items });
      });
    } else {
      // If we're toggling the feature off, we need to set the isFX flag to false on all items.

      for (const newItem of newItems) {
        newItem.isFX = false;
      }
      for (let i = 0; i < newItems.length; i++) {
        const item = newItems[i];

        switch (item.type) {
          case COMPARISON_ITEM_TYPES.PORTFOLIO: {
            const forcedMonthlyRolling: boolean = shouldUseMonthlyRollingInReturns(comparison, state.auth.user);
            promises.push(
              recalcPortfolioItem(dispatch, state, item, forcedMonthlyRolling, comparison.shortHistory, useBaseCurrency, false, comparison.commonInception),
            );
            break;
          }
          default:
            promises.push(Promise.resolve(item));
            break;
        }
        item.isFX = false;
        newItems[i] = item;
      }

      Promise.all(promises).then(items => {
        dispatch({ ...SET_ITEMS, items });
      });
    }

    dispatch({ ...SET_SHOW_IN_BASE_CURRENCY, useBaseCurrency });
  };

export const setMonthEndDataOption =
  (endPeriodValue?: PERIOD_END_DATA_OPTIONS): AsyncResult<void> =>
  async (dispatch, getState) => {
    const { user } = getState().auth;
    const { activeComparison } = getState().comparison;

    if (user == null) {
      return;
    }

    let monthEndDataOptionValue = endPeriodValue;
    let autoMonthEndData = false;

    switch (endPeriodValue) {
      case PERIOD_END_DATA_OPTIONS.RECENT_MONTH_END: {
        if (!userHasPermission(user, PERMISSIONS.MONTH_END_DATA)) {
          return;
        }
        break;
      }
      case PERIOD_END_DATA_OPTIONS.PREVIOUS_MONTH_END: {
        if (!userHasPermission(user, PERMISSIONS.PREVIOUS_MONTH_END_DATA)) {
          return;
        }
        break;
      }
      case PERIOD_END_DATA_OPTIONS.RECENT_QUARTER_END: {
        if (!userHasPermission(user, PERMISSIONS.QUARTER_END_DATA)) {
          return;
        }
        break;
      }
      case PERIOD_END_DATA_OPTIONS.PREVIOUS_QUARTER_END: {
        if (!userHasPermission(user, PERMISSIONS.PREVIOUS_QUARTER_END_DATA)) {
          return;
        }
        break;
      }
      case PERIOD_END_DATA_OPTIONS.AUTO: {
        if (user.preferences?.autoPerformanceAndRiskData == null) {
          return;
        }
        monthEndDataOptionValue = user?.preferences?.autoPerformanceAndRiskData as PERIOD_END_DATA_OPTIONS;
        autoMonthEndData = true;
        break;
      }
    }

    const growthIsYTD = activeComparison?.options?.growth?.isYTD;
    const drawdownIsYTD = activeComparison?.options?.drawdown?.isYTD;

    const originalGrowthStartDate = activeComparison?.options?.growth?.startDate;
    const originalDrawdownStartDate = activeComparison?.options?.drawdown?.startDate;
    const originalGrowthEndDate = activeComparison?.options?.growth?.endDate;
    const originalDrawdownEndDate = activeComparison?.options?.drawdown?.endDate;

    const growthMonthRange = moment(originalGrowthEndDate).diff(moment(originalGrowthStartDate), 'months');
    const drawdownMonthRange = moment(originalDrawdownEndDate).diff(moment(originalDrawdownStartDate), 'months');

    const newEndDate = getMaxEndDate(monthEndDataOptionValue);

    const newGrowthStartDate = originalGrowthStartDate ? moment(newEndDate).subtract(growthMonthRange, 'months').startOf('month').toDate() : null;
    const newDrawdownStartDate = originalDrawdownEndDate ? moment(newEndDate).subtract(drawdownMonthRange, 'months').startOf('month').toDate() : null;

    dispatch(
      setChartOptions({ startDate: growthIsYTD ? moment().startOf('year').toDate() : newGrowthStartDate, endDate: newEndDate }, PERFORMANCE_CHART.GROWTH),
    );
    dispatch(
      setChartOptions({ startDate: drawdownIsYTD ? moment().startOf('year').toDate() : newDrawdownStartDate, endDate: newEndDate }, PERFORMANCE_CHART.DRAWDOWN),
    );

    dispatch(setChartOptions({ endDate: newEndDate }, PERFORMANCE_CHART.INCOME));

    dispatch({ ...SET_MONTH_END_DATA_OPTION, monthEndDataOptionValue, autoMonthEndData });

    const state = getState();

    const { comparison: comparisonState } = state;
    const { itemsFilter: filter } = comparisonState;
    const comparison = getCurrentStateComparison(comparisonState);

    if (!comparison) {
      throw new Error('No current comparison');
    }

    let data = deepClone(comparison) as Comparison;
    data.commonInception = getCommonInception(data, state.comparison.itemsFilter, comparison.shortHistory);
    data = await refreshComparisonData(dispatch, state, data);

    dispatch({ ...SET_ITEMS, items: data.items });
    dispatch({ type: LOAD_COMPARISON.type, comparison: data });
    // Set the filter back to the previous setting as LOAD_COMPARISON resets it
    dispatch({ ...SET_COMPARISON_ITEMS_FILTER, filter });
  };

export const toggleItemFX =
  (item: ComparisonItem, toggled: boolean): Result<void> =>
  (dispatch, getState) => {
    const { comparison: comparisonState } = getState();

    const comparison = getCurrentStateComparison(comparisonState);

    if (!comparison) {
      throw new Error('No current comparison');
    }

    // Clone the items for immutability
    const newItems = deepClone(comparison.items);

    // Set the desired item as expanded (or collapsed)
    const toggledItem = newItems.find(i => i._id === item._id);

    if (!toggledItem || item.isFX === toggled) {
      return;
    }

    toggledItem.isFX = toggled;
    toggledItem.isExpanded = false; // FX cannot be combined with expanded portfolios.

    dispatch({ ...SET_ITEMS, items: newItems });
  };

export const removeScenario =
  (item: ComparisonItem, scenario: IncomeStreamScenario): Result<void> =>
  dispatch => {
    const clone = deepClone(item);

    if (!clone.scenarios || !clone.scenarios.incomeStreamDistribution) {
      throw new Error('No income stream distributions');
    }

    clone.scenarios = {
      ...clone.scenarios,
      incomeStreamDistribution: clone.scenarios.incomeStreamDistribution.filter(s => s.id !== scenario.id),
    };

    // TODO Create generic action to simple reload items
    dispatch({ ...REFRESH_ITEM, item: clone });
  };

export const loadScenarioForEdit =
  (scenario: IncomeStreamScenario): Result<void> =>
  dispatch => {
    dispatch(setComparisonIsFocused(true));
    dispatch({ ...LOAD_SCENARIO_FOR_EDIT, scenario });
  };

export const cancelScenarioEditing =
  (item: ComparisonItem): Result<void> =>
  (dispatch, getState) => {
    const { comparison: ComparisonState } = getState();
    const comparison = getCurrentStateComparison(ComparisonState);

    if (!comparison) {
      throw new Error('No current comparison');
    }

    if (!item.scenarios || !item.scenarios.incomeStreamDistribution) {
      throw new Error('Cannot find original scenario');
    }

    const { editedScenario } = ComparisonState;

    if (editedScenario && editedScenario.id.startsWith('new-')) {
      dispatch(removeScenario(item, editedScenario));
    }

    dispatch({ ...CANCEL_SCENARIO_EDITING });
    dispatch(setComparisonIsFocused(false));
  };

export const createNewScenario =
  (item: ComparisonItem): Result<void> =>
  dispatch => {
    if (!item.scenarios || !item.scenarios.defaultIncomeStream) {
      throw new Error('No default scenario in comparison item');
    }

    const clone = deepClone(item);
    const scenario = deepClone(item.scenarios.defaultIncomeStream);
    scenario.id = generateTempId();
    const originalScenarios = (clone.scenarios && clone.scenarios.incomeStreamDistribution) || [];
    clone.scenarios = {
      ...clone.scenarios,
      incomeStreamDistribution: [...originalScenarios, scenario],
    };

    dispatch(loadScenarioForEdit(scenario));
    dispatch({ ...REFRESH_ITEM, item: clone });
  };

export const setIncomeStreamCapital =
  (item: ComparisonItem, capital: number, startDate: Date, endDate: Date): Result<void> =>
  dispatch => {
    if (!item.scenarios || !item.scenarios.defaultIncomeStream) {
      throw new Error('No scenarios to re-calculate');
    }

    const recalculate = (scenario: IncomeStreamScenario) => {
      const newScenario = { ...scenario, capital };
      return { ...newScenario, data: calculateIncomeStreamData(item.holdings, newScenario, startDate.getTime(), endDate.getTime()) };
    };

    const clone = deepClone(item);
    clone.scenarios = {
      ...clone.scenarios,
      defaultIncomeStream: recalculate(item.scenarios.defaultIncomeStream),
      incomeStreamDistribution: (item.scenarios.incomeStreamDistribution || []).map(recalculate),
    };

    // TODO Create generic action to simple reload items
    dispatch({ ...REFRESH_ITEM, item: clone });
  };

export const updateScenario =
  (scenario: IncomeStreamScenario): Result<void> =>
  dispatch => {
    dispatch({ ...UPDATE_SCENARIO, scenario });
  };

export const saveScenario =
  (item: ComparisonItem, startDate: Date): Result<void> =>
  (dispatch, getState) => {
    const { editedScenario } = getState().comparison;
    const monthEndDataOption = getMonthEndDataOption(getState().comparison);

    const clone = deepClone(item);

    if (!clone.scenarios || !clone.scenarios.incomeStreamDistribution || !editedScenario) {
      throw new Error('No income stream distributions');
    }
    const endDate = getMaxEndDate(monthEndDataOption);
    const { incomeStreamDistribution } = clone.scenarios;
    // TODO merge with capital
    const newScenario = {
      ...editedScenario,
      id: generateBSONId(),
      data: calculateIncomeStreamData(item.holdings, editedScenario, startDate.getTime(), endDate.getTime()),
    };

    const indexToReplace = incomeStreamDistribution.findIndex(s => s.id === editedScenario.id);

    incomeStreamDistribution[indexToReplace] = newScenario;

    // TODO Create generic action to simple reload items
    dispatch({ ...REFRESH_ITEM, item: clone });
    dispatch({ ...CANCEL_SCENARIO_EDITING });
    dispatch(setComparisonIsFocused(false));
  };

export const toggleShortHistory =
  (toggled?: boolean, refreshProposalItems?: boolean): Result<void> =>
  (dispatch, getState) => {
    const state = getState();
    const { comparison: comparisonState, proposalV2 } = state;
    let comparison = getCurrentStateComparison(comparisonState);

    if (!comparison) {
      throw new Error('No current comparison');
    }

    const updateProposalItems = (newItems: ComparisonItem[]) => {
      // Refresh items on proposals V2
      const proposalV2Items = proposalV2.items.map(i => {
        const newItem = newItems.find(item => item._id === i._id);

        if (!newItem) {
          throw new Error('Unable to find matching comparison item for proposal item');
        }

        return newItem;
      });

      dispatch(refreshProposalItemsV2(proposalV2Items));
    };

    const shortHistory = toggled != null ? toggled : !comparison.shortHistory;

    const commonInception = getCommonInception(comparison, comparisonState.itemsFilter, shortHistory);
    if (commonInception?.startTimestamp !== comparison.commonInception?.startTimestamp) {
      dispatch({ ...SET_COMMON_INCEPTION_ITEM, commonInception });
      comparison = getCurrentStateComparison(comparisonState);
    }

    if (!comparison) {
      throw new Error('No current comparison');
    }

    // Clone the items for immutability
    const newItems = deepClone(comparison.items);

    const promises = [] as Promise<ComparisonItem>[];

    for (let i = 0; i < newItems.length; i++) {
      const item = newItems[i];
      const forcedMonthlyRolling: boolean = shouldUseMonthlyRollingInReturns(comparison, state.auth.user);

      switch (item.type) {
        case COMPARISON_ITEM_TYPES.GROUP:
        case COMPARISON_ITEM_TYPES.PORTFOLIO: {
          promises.push(recalcPortfolioItem(dispatch, state, item, forcedMonthlyRolling, shortHistory, comparison.showBaseCurrencies, false, commonInception));
          break;
        }
        case COMPARISON_ITEM_TYPES.BENCHMARK: {
          promises.push(recalcBenchmarkItem(dispatch, item, forcedMonthlyRolling, comparison, shortHistory));
          break;
        }
        default:
          promises.push(Promise.resolve(item));
          break;
      }
      newItems[i] = item;
    }

    Promise.all(promises).then(items => {
      dispatch({ ...SET_ITEMS, items });
      dispatch({ ...SET_SHORT_HISTORY, shortHistory });
      dispatch(setComparisonCorrelations());

      if (refreshProposalItems) {
        updateProposalItems(items);
      }
    });
  };

export const setIsDirty =
  (isDirty: boolean): Result<void> =>
  dispatch => {
    dispatch({ ...SET_IS_DIRTY, value: isDirty });
  };

// Action creator that allows the update of any property of a comparison item holding
// by passing the comparison item to be updated, the holding's id and the data to update
export const updateComparisonItemHolding =
  (item: ComparisonItem, holdingId: string, holdingData: Partial<ComparisonHolding>): Result<void> =>
  (dispatch, getState) => {
    const { comparison: comparisonState } = getState();

    const comparison = getCurrentStateComparison(comparisonState);

    if (!comparison) {
      throw new Error('No current comparison');
    }

    dispatch({ ...SET_ITEM_HOLDING, itemId: item._id, holdingId, holdingData });
  };

export const updateComparisonItemColor =
  (item: ComparisonItem, color: string): Result<void> =>
  (dispatch, getState) => {
    const { comparison: comparisonState } = getState();

    const comparison = getCurrentStateComparison(comparisonState);

    if (!comparison) {
      throw new Error('No current comparison');
    }

    // Clone the items for immutability
    const newItems = deepClone(comparison.items);

    // Set the desired color
    const toggledItem = newItems.find(i => i._id === item._id);

    if (!toggledItem) {
      return;
    }

    toggledItem.color = color;

    dispatch({ ...SET_ITEMS, items: newItems });
  };

export const updateComparisonItemCategory =
  (item: ComparisonItem, category: ComparisonItemCategory | undefined): Result<void> =>
  (dispatch, getState) => {
    const { comparison: comparisonState } = getState();

    const comparison = getCurrentStateComparison(comparisonState);

    if (!comparison) {
      throw new Error('No current comparison');
    }

    // Clone the items for immutability
    const newItems = deepClone(comparison.items);

    // Set the desired category
    const targetItem = newItems.find(i => i._id === item._id);

    if (!targetItem) {
      return;
    }

    if (category) {
      targetItem.category = category;
    } else {
      delete targetItem.category;
    }

    dispatch({ ...SET_ITEMS, items: newItems });
  };

export const setTab =
  (path = ''): Result<void> =>
  dispatch => {
    const tab = path.replace(NAV_PATHS.COMPARISONS, '');
    dispatch({ ...SET_TAB, tab });
  };

export const populateComparisonGroup =
  (name: string, products: ComparisonGroupProduct[], color?: string): AsyncResult<ComparisonItem | null> =>
  async (dispatch, getState) => {
    const state = getState();
    const {
      auth: { user },
      comparison: comparisonState,
    } = state;
    const comparison = getCurrentStateComparison(comparisonState);

    if (!user || !comparison) {
      return null;
    }

    const portfolioItems = comparison.items.filter(item => item.type === COMPARISON_ITEM_TYPES.PORTFOLIO);

    const dbPortfolios = await Promise.all(
      portfolioItems.map(async item => {
        const portfolio = await dispatch(getPortfolioById(item._id, item.portfolioType, item.contact, item.enterprise, undefined, item.source));

        const taxRate = selectCurrentTaxRate(state, portfolio.contact);
        const data = await dispatch(getPortfolioHoldingsData(portfolio));
        const forcedMonthlyRolling: boolean = shouldUseMonthlyRollingInReturns(comparison, state.auth.user);
        return reallocateAndRecalculatePortfolio(portfolio, data, forcedMonthlyRolling, taxRate, comparison.commonInception);
      }),
    );

    const newPortfolios = dbPortfolios.reduce((acc, portfolio) => {
      if (portfolio._id) {
        acc[portfolio._id] = portfolio;
      }
      return acc;
    }, {} as Dictionary<Portfolio>);

    const newItem = await dispatch(combineProductsIntoGroupItem(products, comparison, newPortfolios, state, user.appConfig.appRegion));

    newItem.color = color;
    newItem.name = {
      'en-CA': name,
      'fr-CA': name,
    };

    return newItem;
  };

export const addGroupToComparison =
  (name: string, products: ComparisonGroupProduct[], color?: string): AsyncResult<void> =>
  async dispatch => {
    const newItem = await dispatch(populateComparisonGroup(name, products, color));

    if (newItem) {
      dispatch(addItemAndRecalculateComparison(newItem));
    }
  };

export const updateComparisonGroup =
  (itemId: string, name: string, products: ComparisonGroupProduct[], color?: string): AsyncResult<void> =>
  async dispatch => {
    const newItem = await dispatch(populateComparisonGroup(name, products, color));
    if (newItem) {
      newItem._id = itemId;
      dispatch(replaceItemAndRecalculateComparison(newItem));
    }
  };

export const addItemToGroup =
  (itemId: string, groupId: string, dollars: number, fee?: number): AsyncResult<void> =>
  async (dispatch, getState) => {
    const state = getState();
    const comparison = getCurrentStateComparison(state.comparison);

    if (!comparison) {
      throw new Error('No current comparison');
    }

    const group = comparison.items.find(item => item._id === groupId);
    if (!group) {
      return;
    }

    const products = group.groupItems || [];
    products.push({
      itemId,
      dollars,
      fee,
    });

    const newItem = await dispatch(populateComparisonGroup(formatLocalizedString(group.name), products, group.color));

    if (newItem) {
      newItem._id = group._id;
      dispatch(replaceItemAndRecalculateComparison(newItem));
    }
  };

export const removeItemFromGroup =
  (itemId: string, groupId: string): Result<void> =>
  async (dispatch, getState) => {
    const state = getState();
    const comparison = getCurrentStateComparison(state.comparison);

    if (!comparison) {
      throw new Error('No current comparison');
    }

    const group = comparison.items.find(item => item._id === groupId);
    if (!group) {
      return;
    }

    const products = group.groupItems?.filter(product => product.itemId !== itemId) || [];
    const newItem = await dispatch(populateComparisonGroup(formatLocalizedString(group.name), products, group.color));

    if (newItem) {
      newItem._id = group._id;
      dispatch(replaceItemAndRecalculateComparison(newItem));
    }
  };

export const setComparisonItemsFilter =
  (filter: string[]): Result<void> =>
  dispatch => {
    dispatch({ ...SET_COMPARISON_ITEMS_FILTER, filter });
    dispatch({ ...SET_SHOULD_UPDATE_COMMON_INCEPTION, shouldUpdateCommonInception: true });
  };

export const changeComparisonItemsOrder =
  (itemIds: string[]): Result<void> =>
  (dispatch, getState) => {
    dispatch({ ...CHANGE_COMPARISON_ITEMS_ORDER, itemIds });
  };

export const changeItemId =
  (originalId: string, newId: string): Result<void> =>
  dispatch => {
    dispatch({ ...CHANGE_ITEM_ID, originalId, newId });
  };

export const removeMissingComparisonData =
  (sourceComparison: Comparison): AsyncResult<Comparison> =>
  () =>
    new Promise(async (resolve, reject) => {
      const comparison = deepClone(sourceComparison);

      if (!comparisonHasMissingOrModifiedItems(comparison)) {
        return resolve(comparison);
      }

      const unallocatedPortfolioIds = (comparison.modifiedItems?.filter(m => isUnallocatedPortfolioModification(m)) || []).map(m => m.item._id);
      const removedPlanPortfolioIds = (comparison.modifiedItems?.filter(m => isPlanRemovedPortfolioModification(m)) || []).map(m => m.item._id);

      // Track which indices are being removed for account set updates
      const removedIndices: number[] = [];

      // Remove missing items from items array
      comparison.items = comparison.items.reduce((newItems, item, originalIndex) => {
        const isMissing = comparison.missingItems?.some(missing => missing._id === item._id);
        const isUnallocated = unallocatedPortfolioIds.includes(item._id);
        const isRemovedPlanPortfolio = removedPlanPortfolioIds.includes(item._id);

        if (item.type === COMPARISON_ITEM_TYPES.GROUP) {
          const groupItems = item.groupItems?.filter(groupItem => {
            const groupItemIsUnallocated = unallocatedPortfolioIds.includes(groupItem.itemId);
            const groupItemIsMissing = comparison.missingItems?.some(missing => missing._id === groupItem.itemId);
            return !groupItemIsMissing && !groupItemIsUnallocated;
          });

          if (groupItems && groupItems?.length > 0) {
            newItems.push({
              ...item,
              groupItems,
            });
          } else {
            // Empty group is being removed
            removedIndices.push(originalIndex);
          }
        } else if (isRemovedPlanPortfolio) {
          delete item.planning;
          newItems.push(item);
        } else if (!isMissing && !isUnallocated) {
          newItems.push(item);
        } else {
          // Item is being removed
          removedIndices.push(originalIndex);
        }
        return newItems;
      }, [] as ComparisonItem[]);

      removedIndices.sort((a, b) => b - a);
      for (const removedIndex of removedIndices) {
        comparison.accountSets = updateAccountSetsAfterItemRemoval(comparison.accountSets, removedIndex);
      }

      delete comparison.missingItems;
      comparison.modifiedItems = filterModifiedItemsByKeys(comparison, [
        COMPARISON_MODIFIED_ITEM_KEYS.UNALLOCATED_PORTFOLIO,
        COMPARISON_MODIFIED_ITEM_KEYS.PLANNING_REMOVED,
      ]);

      return resolve(comparison);
    });

export const setComparisonItemFlags =
  (itemId: string, flags: string[]): Result<void> =>
  dispatch => {
    dispatch({ ...SET_COMPARISON_ITEM_FLAGS, itemId, flags });
  };

export const setOriginPath =
  (originPath: LocationState): Result<void> =>
  dispatch => {
    dispatch({ ...SET_ORIGIN_PATH, originPath });
  };

const shouldAutoAddBenchmark = (user?: User | null) => {
  const hasPermission = userHasPermission(user, PERMISSIONS.COMPARISON_AUTO_BENCHMARK);
  const { initialBenchmark } = user?.preferences?.comparisonSettings || getInitialUserPreferences(user);

  if (
    (hasPermission && (!initialBenchmark || initialBenchmark === USER_PREFERENCES_INITIAL_BENCHMARK.CATEGORY_BENCHMARK)) ||
    (!hasPermission && initialBenchmark === USER_PREFERENCES_INITIAL_BENCHMARK.CATEGORY_BENCHMARK)
  ) {
    return true;
  }

  if ((!hasPermission && !initialBenchmark) || (hasPermission && initialBenchmark === USER_PREFERENCES_INITIAL_BENCHMARK.NO_BENCHMARK)) {
    return false;
  }

  return false;
};

export const setCommonInceptionItem = (): Result<void> => async (dispatch, getState) => {
  const state = getState();
  const { comparison: comparisonState } = state;
  const curentComparison = getCurrentStateComparison(comparisonState);

  if (!curentComparison) {
    dispatch({ ...SET_COMMON_INCEPTION_ITEM, commonInception: undefined });
    return Promise.resolve();
  }

  const commonInception = getCommonInception(curentComparison, comparisonState.itemsFilter, curentComparison.shortHistory);

  // If the CI item has not changed, don't refresh
  if (commonInception?.startTimestamp === curentComparison.commonInception?.startTimestamp) {
    return Promise.resolve();
  }

  // this is a complete hard refresh. Not ideal, but I really am running out of time.
  // we want to revisit this and only refresh the affected value (PercentGrowth.CI, riskInfo.CI)
  dispatch({ ...SET_COMMON_INCEPTION_ITEM, commonInception });

  // refresh the comparison
  const newComparison = getCurrentStateComparison(getState().comparison);
  let data = deepClone(newComparison) as Comparison;
  data = await refreshComparisonData(dispatch, state, data);
  dispatch({ ...SET_ITEMS, items: data.items });
  dispatch({ ...SET_SHOULD_UPDATE_COMMON_INCEPTION, shouldUpdateCommonInception: false });
};

export const setProposalLocale =
  (locale: Locale): Result<void> =>
  dispatch => {
    dispatch({ ...SET_PROPOSAL_LOCALE, locale });
  };

export const createOmniExport =
  (comparisonId: string, contactId?: string): AsyncResult<{ comparisonExportId: string }> =>
  () => {
    if (contactId) {
      return api.post(CREATE_OMNI_EXPORT, { id: comparisonId, contactId });
    }
    return api.post(CREATE_OMNI_EXPORT_NO_CONTACT, { id: comparisonId });
  };

export const updateComparisonTaxSettings =
  (taxSettings: TaxSettingsUpdate, avoidDirty = false): Result<void> =>
  dispatch => {
    dispatch({ ...UPDATE_COMPARISON_TAX_SETTINGS, taxSettings, avoidDirty });
  };
